import WebSocket from "isomorphic-ws";
/**
 * This is the entry point of this SDK for your application. Use it to implement the business logic of your application.
 */
export class ClientSdk {
    /**
     * Refreshable user profile class instance.
     */
    userProfile;
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * Balances facade cache.
     * @private
     */
    balancesFacade;
    /**
     * Positions facade cache.
     * @private
     */
    positionsFacade;
    /**
     * Orders facade cache.
     * @private
     */
    ordersFacade;
    /**
     * Quotes facade cache.
     * @private
     */
    quotesFacade;
    /**
     *  Actives facade cache.
     *  @private
     */
    activesFacade;
    /**
     * Blitz options facade cache.
     * @private
     */
    blitzOptionsFacade;
    /**
     * Turbo options facade cache.
     * @private
     */
    turboOptionsFacade;
    /**
     * Binary options facade cache.
     * @private
     */
    binaryOptionsFacade;
    /**
     * Digital options facade cache.
     * @private
     */
    digitalOptionsFacade;
    /**
     * Margin forex facade cache.
     * @private
     */
    marginForexFacade;
    /**
     * Margin cfd facade cache
     * @private
     */
    marginCfdFacade;
    /**
     * Margin crypto facade cache
     * @private
     */
    marginCryptoFacade;
    /**
     * Candles facade cache
     * @private
     */
    candlesFacade;
    /**
     * Creates instance of class.
     * @param userProfile - Information about the user on whose behalf your application is working.
     * @param wsApiClient - Instance of WebSocket API client.
     * @internal
     * @private
     */
    constructor(userProfile, wsApiClient) {
        this.userProfile = userProfile;
        this.wsApiClient = wsApiClient;
    }
    /**
     * Creates instance of SDK entry point class.
     * This method establishes and authenticates connection to system API.
     * @param apiUrl - URL to system API. Usually it has the following format: `wss://ws.trade.{brand_domain}/echo/websocket`.
     * @param platformId - Identification number of your application.
     * @param authMethod - Authentication method used for connection authentication.
     */
    static async create(apiUrl, platformId, authMethod) {
        const wsApiClient = new WsApiClient(apiUrl, platformId, authMethod);
        await wsApiClient.connect();
        const userProfile = await UserProfile.create(wsApiClient);
        return new ClientSdk(userProfile, wsApiClient);
    }
    /**
     * Shuts down instance of SDK entry point class.
     */
    async shutdown() {
        this.wsApiClient.disconnect();
        if (this.blitzOptionsFacade) {
            this.blitzOptionsFacade.close();
        }
        if (this.turboOptionsFacade) {
            this.turboOptionsFacade.close();
        }
        if (this.binaryOptionsFacade) {
            this.binaryOptionsFacade.close();
        }
        if (this.digitalOptionsFacade) {
            this.digitalOptionsFacade.close();
        }
        if (this.positionsFacade) {
            this.positionsFacade.close();
        }
    }
    /**
     * Returns balances facade class.
     */
    async balances() {
        if (!this.balancesFacade) {
            this.balancesFacade = await Balances.create(this.wsApiClient);
        }
        return this.balancesFacade;
    }
    /**
     * Returns positions facade class.
     */
    async positions() {
        if (!this.positionsFacade) {
            const actives = await this.actives();
            this.positionsFacade = await Positions.create(this.wsApiClient, this.userProfile.userId, actives);
        }
        return this.positionsFacade;
    }
    /**
     * Returns actives facade class.
     */
    async actives() {
        if (!this.activesFacade) {
            this.activesFacade = new Actives(this.wsApiClient);
        }
        return this.activesFacade;
    }
    /**
     * Returns quotes facade class.
     */
    async quotes() {
        if (!this.quotesFacade) {
            this.quotesFacade = new Quotes(this.wsApiClient);
        }
        return this.quotesFacade;
    }
    /**
     * Returns blitz options facade class.
     */
    async blitzOptions() {
        if (!this.blitzOptionsFacade) {
            this.blitzOptionsFacade = await BlitzOptions.create(this.wsApiClient);
        }
        return this.blitzOptionsFacade;
    }
    /**
     * Returns turbo options facade class.
     */
    async turboOptions() {
        if (!this.turboOptionsFacade) {
            this.turboOptionsFacade = await TurboOptions.create(this.wsApiClient);
        }
        return this.turboOptionsFacade;
    }
    /**
     * Returns binary options facade class.
     */
    async binaryOptions() {
        if (!this.binaryOptionsFacade) {
            this.binaryOptionsFacade = await BinaryOptions.create(this.wsApiClient);
        }
        return this.binaryOptionsFacade;
    }
    /**
     * Returns digital options facade class.
     */
    async digitalOptions() {
        if (!this.digitalOptionsFacade) {
            this.digitalOptionsFacade = await DigitalOptions.create(this.wsApiClient);
        }
        return this.digitalOptionsFacade;
    }
    /**
     * Returns margin forex facade class.
     */
    async marginForex() {
        if (!this.marginForexFacade) {
            this.marginForexFacade = await MarginForex.create(this.wsApiClient);
        }
        return this.marginForexFacade;
    }
    /**
     * Returns margin cfd facade class.
     */
    async marginCfd() {
        if (!this.marginCfdFacade) {
            this.marginCfdFacade = await MarginCfd.create(this.wsApiClient);
        }
        return this.marginCfdFacade;
    }
    /**
     * Returns margin crypto facade class.
     */
    async marginCrypto() {
        if (!this.marginCryptoFacade) {
            this.marginCryptoFacade = await MarginCrypto.create(this.wsApiClient);
        }
        return this.marginCryptoFacade;
    }
    /**
     * Returns orders facade class.
     */
    async orders() {
        if (!this.ordersFacade) {
            const balances = await this.balances();
            const balanceIds = balances.getBalances().map(balance => balance.id);
            this.ordersFacade = await Orders.create(this.wsApiClient, this.userProfile.userId, balanceIds);
        }
        return this.ordersFacade;
    }
    async candles() {
        if (!this.candlesFacade) {
            this.candlesFacade = new Candles(this.wsApiClient);
        }
        return this.candlesFacade;
    }
    /**
     * Returns ws current time.
     */
    currentTime() {
        return new Date(this.wsApiClient.currentTime.unixMilliTime);
    }
}
/**
 * Implements SSID authentication flow.
 */
export class SsidAuthMethod {
    ssid;
    /**
     * Accepts SSID for authentication.
     *
     * @param ssid - User's session ID.
     */
    constructor(ssid) {
        this.ssid = ssid;
    }
    /**
     * Authenticates client in WebSocket API.
     * @param wsApiClient - Instance of WebSocket API client.
     */
    async authenticateWsApiClient(wsApiClient) {
        const authResponse = await wsApiClient.doRequest(new Authenticate(this.ssid));
        return authResponse.isSuccessful;
    }
}
/**
 * Implements login/password authentication flow.
 */
export class LoginPasswordAuthMethod {
    httpApiUrl;
    login;
    password;
    httpApiClient;
    /**
     * Accepts login and password for authentication.
     *
     * @param httpApiUrl
     * @param login
     * @param password
     */
    constructor(httpApiUrl, login, password) {
        this.httpApiUrl = httpApiUrl;
        this.login = login;
        this.password = password;
        this.httpApiClient = new HttpApiClient(this.httpApiUrl);
    }
    /**
     * Authenticates client in WebSocket API.
     * @param wsApiClient
     */
    async authenticateWsApiClient(wsApiClient) {
        const response = await this.httpApiClient.doRequest(new HttpLoginRequest(this.login, this.password));
        if (response.status === 200 && response.data.code === 'success') {
            const authResponse = await wsApiClient.doRequest(new Authenticate(response.data.ssid));
            return authResponse.isSuccessful;
        }
        return false;
    }
}
/**
 * Don't use this class directly from your code. Use {@link ClientSdk.userProfile} field instead.
 *
 * User profile facade class. Stores information about the user on whose behalf your application is working.
 */
export class UserProfile {
    userId;
    /**
     * Creates instance of class {@link UserProfile}.
     * @param userId - User's identification number.
     * @internal
     * @private
     */
    constructor(userId) {
        this.userId = userId;
    }
    /**
     * Requests information about current user, puts the information to instance of class UserProfile and returns it.
     * @param wsApiClient - Instance of WebSocket API client.
     */
    static async create(wsApiClient) {
        const userProfile = await wsApiClient.doRequest(new CallCoreGetProfileV1());
        return new UserProfile(userProfile.userId);
    }
}
/**
 * Don't use this class directly from your code. Use {@link ClientSdk.balances} static method instead.
 *
 * Balances facade class. Stores information about user's balances. Keeps balances' information up to date.
 */
export class Balances {
    types;
    /**
     * Balances current state.
     * @private
     */
    balances = new Map();
    /**
     * Create instance from DTO.
     * @param types - List of supported balance type ids.
     * @param balancesMsg - Balances data transfer object.
     * @param wsApiClient - Instance of WebSocket API client.
     * @internal
     * @private
     */
    constructor(types, balancesMsg, wsApiClient) {
        this.types = types;
        for (const index in balancesMsg.items) {
            const balance = new Balance(balancesMsg.items[index], wsApiClient);
            this.balances.set(balance.id, balance);
        }
    }
    /**
     * Requests information about user's balances, subscribes on user's balances updates, puts the information to instance of class Balances and returns it.
     * @param wsApiClient - Instance of WebSocket API client.
     */
    static async create(wsApiClient) {
        const types = [1, 4];
        const balancesMsg = await wsApiClient.doRequest(new CallBalancesGetAvailableBalancesV1(types));
        const balances = new Balances(types, balancesMsg, wsApiClient);
        let hasMargin = false;
        for (const [index] of balances.balances) {
            const balance = balances.balances.get(index);
            await wsApiClient.doRequest(new CallSubscribeMarginPortfolioBalanceChangedV1(balance.id));
            if (balance.isMargin) {
                const marginBalance = await wsApiClient.doRequest(new CallMarginGetMarginBalanceV1(balance.id));
                balance.updateMargin(marginBalance);
                hasMargin = true;
            }
        }
        if (hasMargin) {
            await wsApiClient.subscribe(new SubscribeMarginPortfolioBalanceChangedV1(), (event) => {
                balances.updateMarginBalance(event);
            });
        }
        await wsApiClient.subscribe(new SubscribeBalancesBalanceChangedV1(), (event) => {
            balances.updateBalance(event);
        });
        return balances;
    }
    /**
     * Returns list of user's balances. Every item of the list is reference to refreshable object.
     */
    getBalances() {
        const list = [];
        for (const [index] of this.balances) {
            list.push(this.balances.get(index));
        }
        return list;
    }
    /**
     * Returns user's balance with specified ID. If balance does not exist then error will be thrown.
     * @param balanceId - Balance identification number.
     */
    getBalanceById(balanceId) {
        if (!this.balances.has(balanceId)) {
            throw new Error(`balance with id '${balanceId}' is not found`);
        }
        return this.balances.get(balanceId);
    }
    /**
     * Updates instance from DTO.
     * @param balanceChangedMsg - Balances data transfer object.
     * @private
     */
    updateBalance(balanceChangedMsg) {
        if (!this.types.includes(balanceChangedMsg.type)) {
            return;
        }
        if (!this.balances.has(balanceChangedMsg.id)) {
            return;
        }
        this.balances.get(balanceChangedMsg.id).update(balanceChangedMsg);
    }
    /**
     * Updates instance from DTO.
     * @param balanceChangedMsg - Margin balances data transfer object.
     * @private
     */
    updateMarginBalance(balanceChangedMsg) {
        if (!this.types.includes(balanceChangedMsg.type)) {
            return;
        }
        if (!this.balances.has(balanceChangedMsg.id)) {
            return;
        }
        this.balances.get(balanceChangedMsg.id).updateMargin(balanceChangedMsg);
    }
}
/**
 * User's balance refreshable class.
 */
export class Balance {
    /**
     * User's balance identification number.
     */
    id;
    /**
     * User's balance type.
     */
    type;
    /**
     * Current amount of money on user's balance.
     */
    amount;
    /**
     * Current amount of bonuses.
     */
    bonusAmount;
    /**
     * User's balance currency code (ISO 4217).
     */
    currency;
    /**
     * User's identification number.
     */
    userId;
    /**
     * Is margin balance.
     */
    isMargin = false;
    /**
     * Gross Profit and Loss (PnL).
     */
    pnl;
    /**
     * Net Profit and Loss (PnL) after deductions.
     */
    pnlNet;
    /**
     * Total equity in the account.
     */
    equity;
    /**
     * Total equity in USD.
     */
    equityUsd;
    /**
     * Swap charges for holding positions overnight.
     */
    swap;
    /**
     * Dividends received or paid.
     */
    dividends;
    /**
     * Margin used by the account.
     */
    margin;
    /**
     * Available margin for new positions.
     */
    available;
    /**
     * Current amount of money on margin user's balance.
     */
    cash;
    /**
     * Margin level as a percentage.
     */
    marginLevel;
    /**
     * Stop out level where positions are closed to prevent losses.
     */
    stopOutLevel;
    /**
     * Balance updates observer.
     * @private
     */
    onUpdateObserver = new Observable();
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * Initialises the class instance from DTO.
     * @param msg - Balance data transfer object.
     * @param wsApiClient
     * @internal
     * @private
     */
    constructor(msg, wsApiClient) {
        this.id = msg.id;
        this.type = this.convertBalanceType(msg.type);
        this.amount = msg.amount;
        this.bonusAmount = msg.bonusAmount;
        this.currency = msg.currency;
        this.userId = msg.userId;
        this.isMargin = msg.isMargin;
        this.wsApiClient = wsApiClient;
    }
    /**
     * Adds specified callback to balance update subscribers' list.
     * @param callback - Callback will be called for every change of balance.
     */
    subscribeOnUpdate(callback) {
        this.onUpdateObserver.subscribe(callback);
    }
    /**
     * Removes specified callback from balance update subscribers' list.
     * @param callback - Callback for remove.
     */
    unsubscribeOnUpdate(callback) {
        this.onUpdateObserver.unsubscribe(callback);
    }
    /**
     * Resets demo balance to 10000.
     */
    async resetDemoBalance() {
        if (this.type !== BalanceType.Demo) {
            throw new Error('Only demo balance can be reset');
        }
        await this.wsApiClient.doRequest(new CallInternalBillingResetTrainingBalanceV4(this.id, 10000));
    }
    /**
     * Returns available amount for margin trading.
     */
    availableForMarginAmount() {
        if (this.isMargin) {
            return this.available || 0;
        }
        return this.amount;
    }
    /**
     * Returns available amount for options trading.
     */
    availableForOptionsAmount() {
        if (this.isMargin) {
            if (this.available && this.cash) {
                if (this.available < this.cash) {
                    return this.available + this.bonusAmount;
                }
                else {
                    return this.cash + this.bonusAmount;
                }
            }
        }
        return this.amount + this.bonusAmount;
    }
    /**
     * Updates the class instance from DTO.
     * @param msg - Balance data transfer object.
     * @private
     */
    update(msg) {
        this.type = this.convertBalanceType(msg.type);
        this.amount = msg.amount;
        this.bonusAmount = msg.bonusAmount;
        this.currency = msg.currency;
        this.userId = msg.userId;
        this.onUpdateObserver.notify(this);
    }
    updateMargin(msg) {
        this.pnl = msg.pnl;
        this.pnlNet = msg.pnlNet;
        this.equity = msg.equity;
        this.equityUsd = msg.equityUsd;
        this.swap = msg.swap;
        this.dividends = msg.dividends;
        this.margin = msg.margin;
        this.available = msg.available;
        this.cash = msg.cash;
        this.marginLevel = msg.marginLevel;
        this.stopOutLevel = msg.stopOutLevel;
        this.bonusAmount = msg.bonus;
        this.onUpdateObserver.notify(this);
    }
    /**
     * Converts balance type id to text representation.
     * @param typeId - Balance type ID.
     * @private
     */
    convertBalanceType(typeId) {
        switch (typeId) {
            case 1:
                return BalanceType.Real;
            case 4:
                return BalanceType.Demo;
        }
        return undefined;
    }
}
/**
 * Don't use this class directly from your code. Use {@link ClientSdk.candles} static method instead.
 *
 * Candles facade class.
 */
export class Candles {
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * Creates class instance.
     * @param wsApiClient - Instance of WebSocket API client.
     * @internal
     * @private
     */
    constructor(wsApiClient) {
        this.wsApiClient = wsApiClient;
    }
    /**
     * Get candles for specified active.
     * @param activeId
     * @param size
     * @param options
     */
    async getCandles(activeId, size, options = undefined) {
        const response = await this.wsApiClient.doRequest(new CallQuotesHistoryGetCandlesV2({
            activeId,
            size,
            options
        }));
        return response.candles;
    }
}
/**
 * Candle data transfer object.
 */
export class Candle {
    id;
    from;
    to;
    open;
    close;
    min;
    max;
    volume;
    at;
    constructor(data) {
        this.id = data.id;
        this.from = data.from;
        this.to = data.to;
        this.open = data.open;
        this.close = data.close;
        this.min = data.min;
        this.max = data.max;
        this.volume = data.volume;
        this.at = data.at;
    }
}
/**
 * Balance type enum.
 */
export var BalanceType;
(function (BalanceType) {
    /**
     * Real balance type. This type is used for trading on real funds.
     */
    BalanceType["Real"] = "real";
    /**
     * Demo balance type. This type is used for practice/testing on non-real funds. Funds on demo balance can't be withdrawal.
     */
    BalanceType["Demo"] = "demo";
})(BalanceType || (BalanceType = {}));
/**
 * Don't use this class directly from your code. Use {@link ClientSdk.actives} static method instead.
 *
 * Actives facade class. Stores information about actives. Keeps actives' information up to date.
 */
export class Actives {
    wsApiClient;
    activeCache = new Map();
    activeData = new Map();
    constructor(wsApiClient) {
        this.wsApiClient = wsApiClient;
    }
    /**
     * Returns active data with caching.
     * @param activeId - Active ID.
     */
    async getActive(activeId) {
        if (this.activeData.has(activeId)) {
            return this.activeData.get(activeId);
        }
        if (this.activeCache.has(activeId)) {
            return this.activeCache.get(activeId);
        }
        const activePromise = this.wsApiClient.doRequest(new CallGetActiveV5(activeId))
            .then((active) => {
            this.activeData.set(activeId, active);
            this.activeCache.delete(activeId);
            return active;
        })
            .catch((error) => {
            this.activeCache.delete(activeId);
            throw error;
        });
        this.activeCache.set(activeId, activePromise);
        return activePromise;
    }
}
/**
 * Don't use this class directly from your code. Use {@link ClientSdk.quotes} static method instead.
 *
 * Quotes facade class. Stores information about quotes (market data). Keeps quotes' information up to date.
 */
export class Quotes {
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * Quotes current state.
     * @private
     */
    currentQuotes = new Map();
    /**
     * Creates class instance.
     * @param wsApiClient - Instance of WebSocket API client.
     * @internal
     * @private
     */
    constructor(wsApiClient) {
        this.wsApiClient = wsApiClient;
    }
    /**
     * Returns refreshable current quote instance for specified active.
     * @param activeId - Active ID for which the current quote is requested.
     */
    async getCurrentQuoteForActive(activeId) {
        if (this.currentQuotes.has(activeId)) {
            return this.currentQuotes.get(activeId);
        }
        const currentQuote = new CurrentQuote();
        this.currentQuotes.set(activeId, currentQuote);
        await this.wsApiClient.subscribe(new SubscribeQuoteGeneratedV2(activeId), (event) => {
            if (event.activeId !== activeId) {
                return;
            }
            currentQuote.update(event);
        });
        return currentQuote;
    }
}
/**
 * Active's current quote refreshable class.
 */
export class CurrentQuote {
    /**
     * Current quote's active ID.
     */
    activeId;
    /**
     * Current quote's time.
     */
    time;
    /**
     * Current quote's ask (offer) price.
     */
    ask;
    /**
     * Current quote's bid price.
     */
    bid;
    /**
     * Current quote's middle price between ask and bid. `value=(ask+bid)/2`. This price is used for buy/expire option's orders.
     */
    value;
    /**
     * Current quote's phase.
     *
     * `T` - quote is inside regular trading session.
     *
     * `C` - quote is outside any trading session.
     */
    phase;
    /**
     * Position updates observer.
     * @private
     */
    onUpdateObserver = new Observable();
    /**
     * Adds specified callback to current quote update subscribers' list.
     * @param callback - Callback will be called for every change of current quote.
     */
    subscribeOnUpdate(callback) {
        this.onUpdateObserver.subscribe(callback);
    }
    /**
     * Removes specified callback from current quote update subscribers' list.
     * @param callback - Callback for remove.
     */
    unsubscribeOnUpdate(callback) {
        this.onUpdateObserver.unsubscribe(callback);
    }
    /**
     * Updates current quote from DTO.
     * @param msg - Current quote data transfer object.
     * @private
     */
    update(msg) {
        this.activeId = msg.activeId;
        this.time = new Date(msg.time);
        this.ask = msg.ask;
        this.bid = msg.bid;
        this.value = msg.value;
        this.phase = msg.phase;
        this.onUpdateObserver.notify(this);
    }
}
/**
 * Don't use this class directly from your code. Use the following methods instead:
 *
 * * {@link ClientSdk.positions}
 *
 * Positions facade class. Stores information about opened positions. Keeps positions' information up to date.
 */
export class Positions {
    /**
     * Positions current state.
     * @private
     */
    positions = new Map();
    /**
     * Positions history.
     * @private
     */
    positionsHistoryFacade;
    /**
     * Positions' history array.
     * @private
     */
    positionsHistory = [];
    /**
     * Positions' IDs cache.
     * @private
     */
    positionsIds = new Map();
    /**
     * Positions updates observer.
     * @private
     */
    onUpdatePositionObserver = new Observable();
    /**
     * Timer for periodical actives list update.
     * @private
     */
    intervalId;
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * Actives facade.
     * @private
     */
    actives;
    /**
     * Digital options facade.
     * @private
     */
    digitalOptions;
    /**
     * List of supported instrument types.
     * @private
     */
    instrumentTypes = ["digital-option", "binary-option", "turbo-option", "blitz-option", "marginal-cfd", "marginal-crypto", "marginal-forex"];
    /**
     * Just private constructor. Just private constructor. Use {@link Positions.create create} instead.
     * @internal
     * @private
     */
    constructor() {
    }
    /**
     * Subscribes on opened positions' updates, requests current state of opened positions, puts the current state to instance of class Positions and returns it.
     * @param wsApiClient - Instance of WebSocket API client.
     * @param userId - User's identification number.
     * @param actives - Actives facade.
     */
    static async create(wsApiClient, userId, actives) {
        const positionsFacade = new Positions();
        positionsFacade.actives = actives;
        positionsFacade.wsApiClient = wsApiClient;
        positionsFacade.positionsHistoryFacade = new PositionsHistory(wsApiClient, userId, positionsFacade.positionsHistory);
        await positionsFacade.syncOldActivePositions();
        await positionsFacade.subscribePositionChanged(userId);
        await positionsFacade.subscribePositionsState();
        positionsFacade.subscribePositions();
        return positionsFacade;
    }
    /**
     * Subscribes on position's updates.
     *
     * @private
     */
    async subscribePositionChanged(userId) {
        await this.wsApiClient.subscribe(new SubscribePortfolioPositionChangedV3(userId), (event) => {
            this.syncPositionFromEvent(event);
        });
    }
    /**
     * Subscribes on positions states updates.
     * @private
     */
    async subscribePositionsState() {
        this.wsApiClient.subscribe(new SubscribePortfolioPositionsStateV1(), (event) => {
            this.syncPositionsStateFromEvent(event);
        }).then(() => {
        });
        this.intervalId = setInterval(async () => {
            await this.wsApiClient.unsubscribe(new SubscribePortfolioPositionsStateV1());
            await this.wsApiClient.subscribe(new SubscribePortfolioPositionsStateV1(), (event) => {
                this.syncPositionsStateFromEvent(event);
            });
        }, 60000);
    }
    /**
     * Synchronizes old active positions.
     * @private
     */
    async syncOldActivePositions() {
        let offset = 0;
        const limit = 30;
        for (;;) {
            const positionsPage = await this.wsApiClient.doRequest(new CallPortfolioGetPositionsV4(this.instrumentTypes, limit, offset));
            for (const index in positionsPage.positions) {
                this.syncPositionFromResponse(positionsPage.positions[index]);
            }
            if (positionsPage.positions.length < positionsPage.limit) {
                break;
            }
            offset += limit;
        }
    }
    /**
     * Returns list of all positions.
     */
    getAllPositions() {
        const list = [];
        for (const [index] of this.positions) {
            list.push(this.positions.get(index));
        }
        return list;
    }
    /**
     * Returns positions history.
     */
    getPositionsHistory() {
        if (!this.positionsHistoryFacade) {
            throw new Error("Positions history facade is not available");
        }
        return this.positionsHistoryFacade;
    }
    /**
     * Checks if a given order ID matches any of the order IDs associated with a position.
     * @param orderId
     * @param position
     */
    isOrderMatchingPosition(orderId, position) {
        return position.orderIds.includes(orderId);
    }
    /**
     * Adds specified callback to position update subscribers' list.
     * @param callback - Callback will be called for every change of position.
     */
    subscribeOnUpdatePosition(callback) {
        this.onUpdatePositionObserver.subscribe(callback);
    }
    /**
     * Removes specified callback from position update subscribers' list.
     * @param callback - Callback for remove.
     */
    unsubscribeOnUpdatePosition(callback) {
        this.onUpdatePositionObserver.unsubscribe(callback);
    }
    /**
     * Updates instance from DTO.
     * @param msg - Positions state data transfer object.
     * @private
     */
    syncPositionsStateFromEvent(msg) {
        for (const index in msg.positions) {
            const key = `${msg.positions[index].instrumentType}-${msg.positions[index].internalId}`;
            const externalId = this.positionsIds.get(key);
            if (!externalId) {
                continue;
            }
            const position = this.positions.get(externalId);
            if (!position) {
                continue;
            }
            position.syncFromStateEvent(msg.positions[index]);
            this.onUpdatePositionObserver.notify(position);
        }
    }
    /**
     * Updates instance from DTO.
     * @param msg - Position data transfer object.
     * @private
     */
    syncPositionFromResponse(msg) {
        const isNewPosition = !this.positions.has(msg.externalId);
        if (isNewPosition) {
            const position = new Position(this.wsApiClient);
            position.externalId = msg.externalId;
            this.positions.set(msg.externalId, position);
            const key = `${msg.instrumentType}-${msg.internalId}`;
            this.positionsIds.set(key, msg.externalId);
        }
        const position = this.positions.get(msg.externalId);
        position.syncFromResponse(msg);
        this.onUpdatePositionObserver.notify(position);
        if (isNewPosition) {
            this.subscribePositions();
        }
        if (!position.active && position.activeId) {
            this.actives.getActive(position.activeId).then((active) => {
                position.active = active;
            });
        }
        if (position.status === "closed") {
            this.positions.delete(msg.externalId);
            this.positionsIds.delete(`${msg.instrumentType}-${msg.internalId}`);
            this.positionsHistory.unshift(position);
        }
    }
    /**
     * Updates instance from DTO.
     * @param msg - Position data transfer object.
     * @private
     */
    syncPositionFromEvent(msg) {
        const isNewPosition = !this.positions.has(msg.externalId);
        if (isNewPosition) {
            const position = new Position(this.wsApiClient);
            position.externalId = msg.externalId;
            this.positions.set(msg.externalId, position);
            const key = `${msg.instrumentType}-${msg.internalId}`;
            this.positionsIds.set(key, msg.externalId);
        }
        const position = this.positions.get(msg.externalId);
        position.syncFromEvent(msg);
        this.onUpdatePositionObserver.notify(position);
        if (isNewPosition) {
            this.subscribePositions();
        }
        if (!position.active && position.activeId) {
            this.actives.getActive(position.activeId).then((active) => {
                position.active = active;
            });
        }
        if (position.status === "closed") {
            this.positions.delete(msg.externalId);
            this.positionsIds.delete(`${msg.instrumentType}-${msg.internalId}`);
            this.positionsHistory.unshift(position);
        }
    }
    subscribePositions() {
        const internalIds = [];
        for (const position of this.positions.values()) {
            if (position.status === "open") {
                internalIds.push(position.internalId);
            }
        }
        this.wsApiClient.doRequest(new CallPortfolioSubscribePositions("frequent", internalIds)).then(() => {
        });
    }
    /**
     * Closes the instance.
     */
    close() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }
}
/**
 * Don't use this class directly from your code. Use the following methods instead:
 *
 * * {@link ClientSdk.orders}
 *
 * Orders facade class. Stores information about opened orders. Keeps order's information up to date.
 */
export class Orders {
    /**
     * Orders current state.
     * @private
     */
    orders = new Map();
    /**
     * Orders updates observer.
     * @private
     */
    onUpdateOrderObserver = new Observable();
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * List of supported instrument types.
     * @private
     */
    instrumentTypes = ["digital-option", "marginal-cfd", "marginal-crypto", "marginal-forex"];
    /**
     * Just private constructor. Just private constructor. Use {@link Orders.create create} instead.
     * @internal
     * @private
     */
    constructor() {
    }
    /**
     * Subscribes on opened order's updates, requests current state of opened order's, puts the current state to instance of class Orders and returns it.
     * @param wsApiClient - Instance of WebSocket API client.
     * @param userId
     * @param balanceIds
     */
    static async create(wsApiClient, userId, balanceIds) {
        const ordersFacade = new Orders();
        ordersFacade.wsApiClient = wsApiClient;
        for (const index in ordersFacade.instrumentTypes) {
            await ordersFacade.subscribeOrderChanged(userId, ordersFacade.instrumentTypes[index]);
        }
        for (const index in balanceIds) {
            await ordersFacade.syncOldActiveOrders(balanceIds[index]);
        }
        return ordersFacade;
    }
    /**
     * Subscribes on order's updates.
     *
     * @private
     */
    async subscribeOrderChanged(userId, instrumentType) {
        await this.wsApiClient.subscribe(new SubscribePortfolioOrderChangedV2(userId, instrumentType), (event) => {
            if (event.instrumentType === instrumentType) {
                this.syncOrderFromEvent(event);
            }
        });
    }
    /**
     * Synchronizes old active orders.
     * @private
     */
    async syncOldActiveOrders(userBalanceId) {
        const ordersPage = await this.wsApiClient.doRequest(new CallPortfolioGetOrdersV2(userBalanceId));
        for (const index in ordersPage.orders) {
            this.syncOrderFromResponse(ordersPage.orders[index]);
        }
    }
    /**
     * Returns list of all orders.
     */
    getAllOrders() {
        const list = [];
        for (const [index] of this.orders) {
            list.push(this.orders.get(index));
        }
        return list;
    }
    /**
     * Checks if a given position associated with an order.
     * @param position
     * @param order
     */
    isPositionMatchingOrder(position, order) {
        return order.positionId === position.internalId;
    }
    /**
     * Adds specified callback to order update subscribers' list.
     * @param callback - Callback will be called for every change of order.
     */
    subscribeOnUpdateOrder(callback) {
        this.onUpdateOrderObserver.subscribe(callback);
    }
    /**
     * Removes specified callback from order update subscribers' list.
     * @param callback - Callback for remove.
     */
    unsubscribeOnUpdateOrder(callback) {
        this.onUpdateOrderObserver.unsubscribe(callback);
    }
    /**
     * Updates instance from DTO.
     * @param msg - Order data transfer object.
     * @private
     */
    syncOrderFromResponse(msg) {
        if (msg.id === undefined) {
            return;
        }
        const isNewOrder = !this.orders.has(msg.id);
        if (isNewOrder) {
            const order = new Order(this.wsApiClient);
            this.orders.set(msg.id, order);
        }
        const order = this.orders.get(msg.id);
        order.syncFromResponse(msg);
        this.onUpdateOrderObserver.notify(order);
    }
    /**
     * Updates instance from DTO.
     * @param msg - Order data transfer object.
     * @private
     */
    syncOrderFromEvent(msg) {
        if (msg.id === undefined) {
            return;
        }
        const isNewOrder = !this.orders.has(msg.id);
        if (isNewOrder) {
            const order = new Order(this.wsApiClient);
            this.orders.set(msg.id, order);
        }
        const order = this.orders.get(msg.id);
        order.syncFromEvent(msg);
        this.onUpdateOrderObserver.notify(order);
        if (order.status === "filled" || order.status === "canceled" || order.status === "rejected") {
            this.orders.delete(msg.id);
        }
    }
}
export class Order {
    /**
     * Order's identification number.
     */
    id;
    /**
     * Order status.
     */
    status;
    /**
     * Instrument type.
     */
    instrumentType;
    /**
     * Kind of order.
     */
    kind;
    /**
     * Order position ID.
     */
    positionId;
    /**
     * User ID.
     */
    userId;
    /**
     * User's balance ID.
     */
    userBalanceId;
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    constructor(wsApiClient) {
        this.wsApiClient = wsApiClient;
    }
    /**
     * Synchronises order from DTO.
     * @param msg - Order data transfer object.
     * @private
     */
    syncFromResponse(msg) {
        this.id = msg.id;
        this.status = msg.status;
        this.positionId = msg.positionId;
        this.instrumentType = msg.instrumentType;
        this.kind = msg.kind;
        this.userId = msg.userId;
        this.userBalanceId = msg.userBalanceId;
    }
    /**
     * Synchronises order from DTO.
     * @param msg - Order data transfer object.
     * @private
     */
    syncFromEvent(msg) {
        this.id = msg.id;
        this.status = msg.status;
        this.positionId = msg.positionId;
        this.instrumentType = msg.instrumentType;
        this.kind = msg.kind;
        this.userId = msg.userId;
        this.userBalanceId = msg.userBalanceId;
    }
    async cancel() {
        if (!this.id) {
            throw new Error('Order id is not set');
        }
        switch (this.instrumentType) {
            case "marginal-cfd":
                await this.wsApiClient.doRequest(new CallMarginCancelPendingOrderV1("cfd", this.id));
                break;
            case "marginal-crypto":
                await this.wsApiClient.doRequest(new CallMarginCancelPendingOrderV1("crypto", this.id));
                break;
            case "marginal-forex":
                await this.wsApiClient.doRequest(new CallMarginCancelPendingOrderV1("forex", this.id));
                break;
            default:
                throw new Error(`Unsupported instrument type '${this.instrumentType}'`);
        }
    }
}
class PositionsHistory {
    /**
     * Positions history.
     * @private
     */
    positions;
    /**
     * User ID.
     * @private
     */
    userId;
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * Start time for positions history.
     * @private
     */
    startTime;
    /**
     * Limit of positions per page.
     * @private
     */
    limit = 30;
    /**
     * Offset for positions history.
     * @private
     */
    offset = 0;
    /**
     * Flag for previous page.
     * @private
     */
    prevPage = true;
    constructor(wsApiClient, userId, positions) {
        this.wsApiClient = wsApiClient;
        this.userId = userId;
        this.positions = positions;
    }
    /**
     * Fetches previous page of positions history.
     */
    async fetchPrevPage() {
        if (!this.startTime) {
            this.startTime = Math.trunc(this.wsApiClient.currentTime.unixMilliTime / 1000);
        }
        const positionsPage = await this.wsApiClient.doRequest(new CallPortfolioGetHistoryPositionsV2({
            userId: this.userId,
            limit: this.limit,
            offset: this.offset,
            end: this.startTime,
            instrumentTypes: ["digital-option", "binary-option", "turbo-option", "blitz-option"],
        }));
        for (const index in positionsPage.positions) {
            const position = new Position(this.wsApiClient);
            position.syncFromHistoryResponse(positionsPage.positions[index]);
            this.positions.push(position);
        }
        if (positionsPage.positions.length < positionsPage.limit) {
            this.prevPage = false;
        }
        this.offset += this.limit;
    }
    /**
     * Checks if previous page exists.
     */
    hasPrevPage() {
        return this.prevPage;
    }
    /**
     * Returns positions history.
     */
    getPositions() {
        const positions = [];
        for (let i = 0; i < this.positions.length; i += 1) {
            positions[i] = this.positions[i];
        }
        return positions;
    }
}
/**
 * Position refreshable class.
 */
export class Position {
    /**
     * Position's identification number ( position external ID ).
     */
    externalId;
    /**
     * Position's internal ID. ( Positions across different instrument types can have the same internal_id )
     */
    internalId;
    /**
     * Position's active ID.
     */
    activeId;
    /**
     * Position's balance ID.
     */
    balanceId;
    /**
     * Amount of profit by the position.
     */
    closeProfit;
    /**
     * Quote price at which the position was closed.
     */
    closeQuote;
    /**
     * Position's close reason.
     */
    closeReason;
    /**
     * Current quote price.
     */
    currentQuote;
    /**
     * The time at which the position was closed.
     */
    closeTime;
    /**
     * Expected profit for the position.
     */
    expectedProfit;
    /**
     * Type of trading instrument.
     */
    instrumentType;
    /**
     * The amount of the initial investment.
     */
    invest;
    /**
     * Quote price at which the position was opened.
     */
    openQuote;
    /**
     * The time at which the position was opened.
     */
    openTime;
    /**
     * Expected PnL for the position.
     */
    pnl;
    /**
     * Expected PnL Net for the position.
     */
    pnlNet;
    /**
     * PnL with which the position was closed.
     */
    pnlRealized;
    /**
     * Quote time at which the position was opened.
     */
    quoteTimestamp;
    /**
     * Current quote time.
     */
    currentQuoteTimestamp;
    /**
     * Position's status.
     */
    status;
    /**
     * Position's user ID.
     */
    userId;
    /**
     * Realized profit from selling the position at this moment.
     */
    sellProfit;
    /**
     * List of order IDs.
     */
    orderIds = [];
    /**
     * Active information.
     */
    active;
    /**
     * Expiration time for the position.
     */
    expirationTime;
    /**
     * Direction of the position.
     */
    direction;
    /**
     * Version of position. Used for filter old versions of position's state.
     * @private
     */
    version;
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    constructor(wsApiClient) {
        this.wsApiClient = wsApiClient;
    }
    /**
     * Synchronises position from DTO.
     * @param msg - Position data transfer object.
     * @private
     */
    syncFromResponse(msg) {
        this.externalId = msg.externalId;
        this.internalId = msg.internalId;
        this.activeId = msg.activeId;
        this.balanceId = msg.userBalanceId;
        this.expectedProfit = msg.expectedProfit;
        this.instrumentType = msg.instrumentType;
        this.invest = msg.invest;
        this.openQuote = msg.openQuote;
        this.openTime = new Date(msg.openTime);
        this.pnl = msg.pnl;
        this.quoteTimestamp = msg.quoteTimestamp !== undefined ? new Date(msg.quoteTimestamp) : undefined;
        this.status = msg.status;
        this.userId = msg.userId;
        this.orderIds = msg.orderIds;
        this.direction = msg.direction;
        this.expirationTime = msg.expirationTime !== undefined ? new Date(msg.expirationTime) : undefined;
    }
    /**
     * Synchronises position from DTO.
     * @param msg - Position data transfer object.
     * @private
     */
    syncFromHistoryResponse(msg) {
        this.externalId = msg.externalId;
        this.internalId = msg.internalId;
        this.activeId = msg.activeId;
        this.balanceId = msg.userBalanceId;
        this.instrumentType = msg.instrumentType;
        this.invest = msg.invest;
        this.openQuote = msg.openQuote;
        this.openTime = new Date(msg.openTime);
        this.closeProfit = msg.closeProfit;
        this.closeQuote = msg.closeQuote;
        this.closeReason = msg.closeReason;
        this.closeTime = msg.closeTime !== undefined ? new Date(msg.closeTime) : undefined;
        this.pnl = msg.pnl;
        this.pnlRealized = msg.pnlRealized;
        this.pnlNet = msg.pnlNet;
        this.status = msg.status;
        this.userId = msg.userId;
        this.orderIds = msg.orderIds;
    }
    /**
     * Synchronises position from DTO.
     * @param msg - Position data transfer object.
     * @private
     */
    syncFromEvent(msg) {
        if (this.version !== undefined && msg.version !== undefined && this.version >= msg.version) {
            return;
        }
        this.internalId = msg.internalId;
        this.activeId = msg.activeId;
        this.balanceId = msg.userBalanceId;
        this.closeProfit = msg.closeProfit;
        this.closeQuote = msg.closeQuote;
        this.closeReason = msg.closeReason;
        this.closeTime = msg.closeTime !== undefined ? new Date(msg.closeTime) : undefined;
        this.expectedProfit = msg.expectedProfit;
        this.version = msg.version;
        this.instrumentType = msg.instrumentType;
        this.invest = msg.invest;
        this.openQuote = msg.openQuote;
        this.openTime = new Date(msg.openTime);
        this.pnl = msg.pnl;
        this.pnlRealized = msg.pnlRealized;
        this.quoteTimestamp = msg.quoteTimestamp !== undefined ? new Date(msg.quoteTimestamp) : undefined;
        this.status = msg.status;
        this.userId = msg.userId;
        this.orderIds = msg.orderIds;
        this.direction = msg.direction;
        this.expirationTime = msg.expirationTime !== undefined ? new Date(msg.expirationTime) : undefined;
    }
    /**
     * Synchronises position from DTO.
     * @param msg - Position state data transfer object.
     * @private
     */
    syncFromStateEvent(msg) {
        this.sellProfit = msg.sellProfit;
        this.currentQuote = msg.currentPrice;
        this.currentQuoteTimestamp = msg.quoteTimestamp !== undefined ? new Date(msg.quoteTimestamp) : undefined;
        this.pnl = msg.pnl;
        this.pnlNet = msg.pnlNet;
        this.expectedProfit = msg.expectedProfit;
    }
    async sell() {
        let promise;
        switch (this.instrumentType) {
            case InstrumentType.TurboOption:
            case InstrumentType.BinaryOption:
                promise = this.wsApiClient.doRequest(new CallBinaryOptionsSellOptionsV3([this.externalId]));
                break;
            case InstrumentType.DigitalOption:
                promise = this.wsApiClient.doRequest(new CallDigitalOptionsClosePositionV1(this.externalId));
                break;
            case InstrumentType.BlitzOption:
                throw new Error("Blitz options are not supported");
            case InstrumentType.MarginCfd:
                promise = this.wsApiClient.doRequest(new CallMarginClosePositionV1("cfd", this.externalId));
                break;
            case InstrumentType.MarginCrypto:
                promise = this.wsApiClient.doRequest(new CallMarginClosePositionV1("crypto", this.externalId));
                break;
            case InstrumentType.MarginForex:
                promise = this.wsApiClient.doRequest(new CallMarginClosePositionV1("forex", this.externalId));
                break;
            default:
                throw new Error(`Unknown instrument type ${this.instrumentType}`);
        }
        const result = await promise;
        if (!result.success) {
            throw new Error(result.reason);
        }
    }
}
/**
 * Don't use this class directly from your code. Use {@link ClientSdk.blitzOptions} static method instead.
 *
 * Blitz options facade class.
 */
export class BlitzOptions {
    /**
     * Actives current state.
     * @private
     */
    actives = new Map();
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * Timer for periodical actives list update.
     * @private
     */
    intervalId;
    /**
     * Creates instance from DTO.
     * @param activesMsg - actives data transfer object.
     * @param wsApiClient - Instance of WebSocket API client.
     * @internal
     * @private
     */
    constructor(activesMsg, wsApiClient) {
        this.wsApiClient = wsApiClient;
        this.updateActives(activesMsg);
    }
    /**
     * Requests information about blitz options actives, runs timer for periodical actives list update, puts the information to instance of class BlitzOptions and returns it.
     * @param wsApiClient - Instance of WebSocket API client.
     */
    static async create(wsApiClient) {
        const initializationData = await wsApiClient.doRequest(new CallBinaryOptionsGetInitializationDataV3());
        const blitzOptions = new BlitzOptions(initializationData.blitzActives, wsApiClient);
        blitzOptions.intervalId = setInterval(async () => {
            const response = await wsApiClient.doRequest(new CallBinaryOptionsGetInitializationDataV3());
            blitzOptions.updateActives(response.blitzActives);
        }, 600000);
        return blitzOptions;
    }
    /**
     * Returns list of blitz options actives.
     */
    getActives() {
        const list = [];
        for (const [index] of this.actives) {
            list.push(this.actives.get(index));
        }
        return list;
    }
    /**
     * Returns refreshable instance of class BlitzOptionsActive by specified active ID. If active doesn't exist then error will be thrown.
     * @param activeId - Active identification number.
     */
    getActive(activeId) {
        if (!this.actives.has(activeId)) {
            throw new Error(`active with id '${activeId}' is not found`);
        }
        return this.actives.get(activeId);
    }
    /**
     * Makes request for buy blitz option.
     * @param active - The asset for which the option is purchased.
     * @param direction - Direction of price change.
     * @param expirationSize - How many seconds after buying an option should the option expire. A list of available expiration sizes can be found {@link BlitzOptionsActive.expirationTimes}.
     * @param price - The amount of the initial investment.
     * @param balance - The balance from which the initial investment will be written off and upon successful closing of the position, profit will be credited to this balance.
     */
    async buy(active, direction, expirationSize, price, balance) {
        const request = new CallBinaryOptionsOpenBlitzOptionV2(active.id, direction, expirationSize, price, balance.id, 100 - active.profitCommissionPercent);
        const response = await this.wsApiClient.doRequest(request);
        return new BlitzOptionsOption(response);
    }
    /**
     * Update instance from DTO.
     * @param activesMsg - Actives data transfer object.
     * @private
     */
    updateActives(activesMsg) {
        for (const index in activesMsg) {
            if (this.actives.has(activesMsg[index].id)) {
                this.actives.get(activesMsg[index].id).update(activesMsg[index]);
            }
            else {
                this.actives.set(activesMsg[index].id, new BlitzOptionsActive(activesMsg[index]));
            }
            // @todo mark absent actives as deleted.
        }
    }
    /**
     * Closes the instance.
     */
    close() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }
}
/**
 * Instrument types.
 */
export var InstrumentType;
(function (InstrumentType) {
    InstrumentType["BinaryOption"] = "binary-option";
    InstrumentType["DigitalOption"] = "digital-option";
    InstrumentType["TurboOption"] = "turbo-option";
    InstrumentType["BlitzOption"] = "blitz-option";
    InstrumentType["MarginForex"] = "marginal-forex";
    InstrumentType["MarginCfd"] = "marginal-cfd";
    InstrumentType["MarginCrypto"] = "marginal-crypto";
})(InstrumentType || (InstrumentType = {}));
/**
 * Margin Trading TPSL types.
 */
export var MarginTradingTPSLType;
(function (MarginTradingTPSLType) {
    MarginTradingTPSLType["Price"] = "price";
    MarginTradingTPSLType["Pips"] = "pips";
    MarginTradingTPSLType["Delta"] = "delta";
    MarginTradingTPSLType["Pnl"] = "pnl";
})(MarginTradingTPSLType || (MarginTradingTPSLType = {}));
/**
 * Margin Trading TPSL class.
 */
export class MarginTradingTPSL {
    type;
    value;
    constructor(type, value) {
        this.type = type;
        this.value = value;
    }
}
/**
 * Blitz options direction of price change.
 */
export var BlitzOptionsDirection;
(function (BlitzOptionsDirection) {
    /**
     * The decision is that the price will go up.
     */
    BlitzOptionsDirection["Call"] = "call";
    /**
     * The decision is that the price will go down.
     */
    BlitzOptionsDirection["Put"] = "put";
})(BlitzOptionsDirection || (BlitzOptionsDirection = {}));
/**
 * Blitz options active refreshable class.
 */
export class BlitzOptionsActive {
    /**
     * Active's identification number.
     */
    id;
    /**
     * Active's ticker (symbol).
     */
    ticker;
    /**
     * Is trading suspended on the active.
     */
    isSuspended;
    /**
     * Expiration times (sizes) available for the active.
     */
    expirationTimes;
    /**
     * The commission is taken from 100% of the profit. Therefore, income percent can be calculated using the following formula: `profitIncomePercent=100-profitCommissionPercent`.
     */
    profitCommissionPercent;
    /**
     * Active's trading schedule.
     */
    schedule = [];
    /**
     * Creates class instance from DTO.
     * @param msg - Actives' data transfer object.
     * @internal
     * @private
     */
    constructor(msg) {
        this.id = msg.id;
        this.ticker = msg.ticker;
        this.isSuspended = msg.isSuspended;
        this.expirationTimes = msg.expirationTimes;
        this.profitCommissionPercent = msg.profitCommission;
        this.schedule = [];
        for (const index in msg.schedule) {
            this.schedule.push(new BlitzOptionsActiveTradingSession(msg.schedule[index][0], msg.schedule[index][1]));
        }
    }
    /**
     * Checks whether an option on an active can be purchased at a specified time.
     * @param at - Time for which the check is performed.
     */
    canBeBoughtAt(at) {
        if (this.isSuspended) {
            return false;
        }
        const atUnixTimeMilli = at.getTime();
        return this.schedule.findIndex((session) => {
            return session.from.getTime() <= atUnixTimeMilli && session.to.getTime() >= atUnixTimeMilli;
        }) >= 0;
    }
    /**
     * Updates the instance from DTO.
     * @param msg - Active's data transfer object.
     * @private
     */
    update(msg) {
        this.ticker = msg.ticker;
        this.expirationTimes = msg.expirationTimes;
        this.isSuspended = msg.isSuspended;
        this.profitCommissionPercent = msg.profitCommission;
        this.schedule = [];
        for (const index in msg.schedule) {
            this.schedule.push(new BlitzOptionsActiveTradingSession(msg.schedule[index][0], msg.schedule[index][1]));
        }
    }
}
/**
 * Blitz options active trading session class.
 */
export class BlitzOptionsActiveTradingSession {
    /**
     * Start time of trading session.
     */
    from;
    /**
     * End time of trading session.
     */
    to;
    /**
     * Initialises class instance from DTO.
     * @param fromTs - Unix time of session start.
     * @param toTs - Unix time of session end.
     */
    constructor(fromTs, toTs) {
        this.from = new Date(fromTs * 1000);
        this.to = new Date(toTs * 1000);
    }
}
/**
 * Blitz options option order class.
 */
export class BlitzOptionsOption {
    /**
     * Option's ID.
     */
    id;
    /**
     * Option's active ID.
     */
    activeId;
    /**
     * Option's price direction.
     */
    direction;
    /**
     * Option's expiration time.
     */
    expiredAt;
    /**
     * Option's amount of the initial investment.
     */
    price;
    /**
     * Option's profit income percent.
     */
    profitIncomePercent;
    /**
     * The time when the option was purchased.
     */
    openedAt;
    /**
     * The {@link CurrentQuote.value value} of the quote at which the option was purchased.
     */
    openQuoteValue;
    /**
     * Creates class instance from DTO.
     * @param msg - Option's data transfer object.
     * @internal
     * @private
     */
    constructor(msg) {
        this.id = msg.id;
        this.activeId = msg.activeId;
        this.direction = msg.direction;
        this.price = msg.price;
        this.expiredAt = new Date(msg.expired * 1000);
        this.profitIncomePercent = msg.profitIncome;
        this.openedAt = new Date(msg.timeRate * 1000);
        this.openQuoteValue = msg.value;
    }
}
/**
 * Don't use this class directly from your code. Use {@link ClientSdk.turboOptions} static method instead.
 *
 * Turbo options facade class.
 */
export class TurboOptions {
    /**
     * Actives current state.
     * @private
     */
    actives = new Map();
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * Timer for periodical actives list update.
     * @private
     */
    intervalId;
    /**
     * Creates class instance.
     * @param activesMsg - Actives data transfer object.
     * @param wsApiClient - Instance of WebSocket API client.
     * @internal
     * @private
     */
    constructor(activesMsg, wsApiClient) {
        this.wsApiClient = wsApiClient;
        this.updateActives(activesMsg);
    }
    /**
     * Requests information about turbo options actives, runs timer for periodical actives list update, puts the information to instance of class TurboOptions and returns it.
     * @param wsApiClient - Instance of WebSocket API client.
     */
    static async create(wsApiClient) {
        const initializationData = await wsApiClient.doRequest(new CallBinaryOptionsGetInitializationDataV3());
        const turboOptions = new TurboOptions(initializationData.turboActives, wsApiClient);
        turboOptions.intervalId = setInterval(async () => {
            const response = await wsApiClient.doRequest(new CallBinaryOptionsGetInitializationDataV3());
            turboOptions.updateActives(response.turboActives);
        }, 600000);
        return turboOptions;
    }
    /**
     * Returns list of turbo options actives.
     */
    getActives() {
        const list = [];
        for (const [index] of this.actives) {
            list.push(this.actives.get(index));
        }
        return list;
    }
    /**
     * Returns refreshable instance of class TurboOptionsActive by specified active ID. If active doesn't exist then error will be thrown.
     * @param activeId - Active identification number.
     */
    getActive(activeId) {
        if (!this.actives.has(activeId)) {
            throw new Error(`active with id '${activeId}' is not found`);
        }
        return this.actives.get(activeId);
    }
    /**
     * Makes request for buy turbo option.
     * @param instrument - The instrument for which the option is purchased.
     * @param direction - Direction of price change.
     * @param price - The amount of the initial investment.
     * @param balance - The balance from which the initial investment will be written off and upon successful closing of the position, profit will be credited to this balance.
     */
    async buy(instrument, direction, price, balance) {
        const request = new CallBinaryOptionsOpenTurboOptionV2(instrument.activeId, Math.trunc(instrument.expiredAt.getTime() / 1000), direction, price, balance.id, 100 - instrument.profitCommissionPercent);
        const response = await this.wsApiClient.doRequest(request);
        return new TurboOptionsOption(response);
    }
    /**
     * Updates instance from DTO.
     * @param activesMsg - Actives data transfer object.
     * @private
     */
    updateActives(activesMsg) {
        for (const index in activesMsg) {
            if (this.actives.has(activesMsg[index].id)) {
                this.actives.get(activesMsg[index].id).update(activesMsg[index]);
            }
            else {
                this.actives.set(activesMsg[index].id, new TurboOptionsActive(activesMsg[index], this.wsApiClient.currentTime));
            }
            // @todo mark absent actives as deleted.
        }
    }
    /**
     * Closes the instance.
     */
    close() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
        this.actives.forEach((active) => {
            active.close();
        });
    }
}
/**
 * Turbo options direction of price change.
 */
export var TurboOptionsDirection;
(function (TurboOptionsDirection) {
    /**
     * The decision is that the price will go up.
     */
    TurboOptionsDirection["Call"] = "call";
    /**
     * The decision is that the price will go down.
     */
    TurboOptionsDirection["Put"] = "put";
})(TurboOptionsDirection || (TurboOptionsDirection = {}));
/**
 * Turbo options active refreshable class.
 */
export class TurboOptionsActive {
    /**
     * Active's identification number.
     */
    id;
    /**
     * How many seconds before expiration time the ability to buyback options for this active will not be allowed.
     */
    buybackDeadtime;
    /**
     * How many seconds before expiration time the ability to purchase options for this active will not be allowed.
     */
    deadtime;
    /**
     * Active's ticker (symbol).
     */
    ticker;
    /**
     * Is buyback available in the active.
     */
    isBuyback;
    /**
     * Is trading suspended on the active.
     */
    isSuspended;
    /**
     * Count of nearest options available for the active.
     */
    optionCount;
    /**
     * Expiration times (sizes) available for the active.
     */
    expirationTimes;
    /**
     * The commission is taken from 100% of the profit. Therefore, income percent can be calculated using the following formula: `profitIncomePercent=100-profitCommissionPercent`.
     */
    profitCommissionPercent;
    /**
     * Active's trading schedule.
     */
    schedule = [];
    /**
     * An object with the current time obtained from WebSocket API.
     * @private
     */
    currentTime;
    /**
     * Instruments facade class instance.
     * @private
     */
    instrumentsFacade;
    /**
     * Creates instance from DTO.
     * @param msg - Active's data transfer object.
     * @param currentTime - An object with the current time obtained from WebSocket API.
     * @internal
     * @private
     */
    constructor(msg, currentTime) {
        this.id = msg.id;
        this.deadtime = msg.deadtime;
        this.buybackDeadtime = msg.buybackDeadtime;
        this.isBuyback = msg.isBuyback;
        this.ticker = msg.ticker;
        this.optionCount = msg.optionCount;
        this.isSuspended = msg.isSuspended;
        this.profitCommissionPercent = msg.profitCommission;
        this.expirationTimes = msg.expirationTimes;
        this.schedule = [];
        for (const index in msg.schedule) {
            this.schedule.push(new TurboOptionsActiveTradingSession(msg.schedule[index][0], msg.schedule[index][1]));
        }
        this.currentTime = currentTime;
    }
    /**
     * Returns turbo options active's instruments facade.
     */
    async instruments() {
        if (!this.instrumentsFacade) {
            this.instrumentsFacade = await TurboOptionsActiveInstruments.create(this, this.currentTime);
        }
        return this.instrumentsFacade;
    }
    /**
     * Updates the instance from DTO.
     * @param msg - Active's data transfer object.
     * @private
     */
    update(msg) {
        this.deadtime = msg.deadtime;
        this.buybackDeadtime = msg.buybackDeadtime;
        this.ticker = msg.ticker;
        this.isSuspended = msg.isSuspended;
        this.isBuyback = msg.isBuyback;
        this.profitCommissionPercent = msg.profitCommission;
        this.optionCount = msg.optionCount;
        this.expirationTimes = msg.expirationTimes;
        this.schedule = [];
        for (const index in msg.schedule) {
            this.schedule.push(new TurboOptionsActiveTradingSession(msg.schedule[index][0], msg.schedule[index][1]));
        }
    }
    /**
     * Checks whether an option on an active can be purchased at a specified time.
     * @param at - Time for which the check is performed.
     */
    canBeBoughtAt(at) {
        if (this.isSuspended) {
            return false;
        }
        const atUnixTimeMilli = at.getTime();
        return this.schedule.findIndex((session) => {
            return session.from.getTime() <= atUnixTimeMilli && session.to.getTime() >= atUnixTimeMilli;
        }) >= 0;
    }
    /**
     * Closes the instance.
     */
    close() {
        if (this.instrumentsFacade) {
            this.instrumentsFacade.close();
        }
    }
}
/**
 * Turbo options active trading session class.
 */
export class TurboOptionsActiveTradingSession {
    /**
     * Start time of trading session.
     */
    from;
    /**
     * End time of trading session.
     */
    to;
    /**
     * Initialises class instance from DTO.
     * @param fromTs - Unix time of session start.
     * @param toTs - Unix time of session end.
     */
    constructor(fromTs, toTs) {
        this.from = new Date(fromTs * 1000);
        this.to = new Date(toTs * 1000);
    }
}
/**
 * Turbo options active's instruments facade class. Periodically generates active's instruments based on active's settings.
 */
export class TurboOptionsActiveInstruments {
    active;
    currentTime;
    /**
     * Instruments current state.
     * @private
     */
    instruments = new Map();
    /**
     * Timer for periodical actives list update.
     * @private
     */
    intervalId;
    /**
     * Creates class instance.
     * @param active - Active.
     * @param currentTime - An object with the current time obtained from WebSocket API.
     * @internal
     * @private
     */
    constructor(active, currentTime) {
        this.active = active;
        this.currentTime = currentTime;
    }
    /**
     * Runs timer for periodical active's instruments list generation, creates instance of this class and returns it.
     * @param active - The active for which instruments are generated.
     * @param currentTime - An object with the current time obtained from WebSocket API.
     */
    static async create(active, currentTime) {
        const instrumentsFacade = new TurboOptionsActiveInstruments(active, currentTime);
        instrumentsFacade.generateInstruments();
        instrumentsFacade.intervalId = setInterval(() => {
            instrumentsFacade.generateInstruments();
        }, 30000);
        return instrumentsFacade;
    }
    /**
     * Returns list of instruments available for buy at specified time.
     * @param at - Time for which the check is performed.
     */
    getAvailableForBuyAt(at) {
        const list = [];
        for (const [index] of this.instruments) {
            if (this.instruments.get(index).isAvailableForBuyAt(at)) {
                list.push(this.instruments.get(index));
            }
        }
        return list;
    }
    /**
     * Generates instruments.
     * @private
     */
    generateInstruments() {
        if (!this.active.canBeBoughtAt(new Date(this.currentTime.unixMilliTime))) {
            return;
        }
        const generatedInstrumentsKeys = [];
        const nowUnixTime = Math.trunc(this.currentTime.unixMilliTime / 1000);
        for (const index in this.active.expirationTimes) {
            const expirationSize = this.active.expirationTimes[index];
            let instrumentExpirationUnixTime = nowUnixTime + expirationSize - nowUnixTime % expirationSize;
            for (let i = 0; i < this.active.optionCount; i++) {
                const key = `${this.active.id},${expirationSize},${instrumentExpirationUnixTime}`;
                generatedInstrumentsKeys.push(key);
                if (!this.instruments.has(key)) {
                    this.instruments.set(key, new TurboOptionsActiveInstrument(this.active.id, expirationSize, new Date(instrumentExpirationUnixTime * 1000), this.active.deadtime, this.active.profitCommissionPercent));
                }
                else {
                    this.instruments.get(key).update(this.active.deadtime);
                }
                instrumentExpirationUnixTime += expirationSize;
            }
        }
        for (const [index] of this.instruments) {
            if (!generatedInstrumentsKeys.includes(index)) {
                this.instruments.delete(index);
            }
        }
    }
    /**
     * Closes the instance.
     */
    close() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }
}
/**
 * Turbo options active's instrument refreshable class.
 */
export class TurboOptionsActiveInstrument {
    activeId;
    expirationSize;
    expiredAt;
    deadtime;
    profitCommissionPercent;
    /**
     * Creates instance of the class.
     * @param activeId - Instrument's active ID.
     * @param expirationSize - Instrument's expiration size.
     * @param expiredAt - The time when the instrument will be expired.
     * @param deadtime - How many seconds before expiration time the ability to purchase options for this instrument will not be allowed.
     * @param profitCommissionPercent - The commission is taken from 100% of the profit. Therefore, income percent can be calculated using the following formula: `profitIncomePercent=100-profitCommissionPercent`.
     * @internal
     * @private
     */
    constructor(activeId, expirationSize, expiredAt, deadtime, profitCommissionPercent) {
        this.activeId = activeId;
        this.expirationSize = expirationSize;
        this.expiredAt = expiredAt;
        this.deadtime = deadtime;
        this.profitCommissionPercent = profitCommissionPercent;
    }
    /**
     * Checks availability for buy option at specified time.
     * @param at - Time for which the check is performed.
     */
    isAvailableForBuyAt(at) {
        return this.purchaseEndTime().getTime() > at.getTime();
    }
    /**
     * Returns the time until which it is possible to open trades that will fall into the current expiration.
     * @returns {Date}
     */
    purchaseEndTime() {
        return new Date(this.expiredAt.getTime() - this.deadtime * 1000);
    }
    /**
     * Returns the remaining duration in milliseconds for which it is possible to purchase options.
     * @param {Date} currentTime - The current time.
     * @returns {number} - The remaining duration in milliseconds.
     */
    durationRemainingForPurchase(currentTime) {
        return this.purchaseEndTime().getTime() - currentTime.getTime();
    }
    /**
     * Updates the instance from DTO.
     * @param deadtime - How many seconds before expiration time the ability to purchase options for this instrument will not be allowed.
     * @private
     */
    update(deadtime) {
        this.deadtime = deadtime;
    }
}
/**
 * Turbo options option order class.
 */
export class TurboOptionsOption {
    /**
     * Option's ID.
     */
    id;
    /**
     * Option's active ID.
     */
    activeId;
    /**
     * Option's price direction.
     */
    direction;
    /**
     * Option's expiration time.
     */
    expiredAt;
    /**
     * Option's amount of the initial investment.
     */
    price;
    /**
     * Option's profit income percent.
     */
    profitIncomePercent;
    /**
     * The time when the option was purchased.
     */
    openedAt;
    /**
     * The {@link CurrentQuote.value value} of the quote at which the option was purchased.
     */
    openQuoteValue;
    /**
     * Create instance from DTO.
     * @param msg - Option's data transfer object.
     * @internal
     * @private
     */
    constructor(msg) {
        this.id = msg.id;
        this.activeId = msg.activeId;
        this.direction = msg.direction;
        this.price = msg.price;
        this.profitIncomePercent = msg.profitIncome;
        this.expiredAt = new Date(msg.expired * 1000);
        this.openedAt = new Date(msg.timeRate * 1000);
        this.openQuoteValue = msg.value;
    }
}
/**
 * Don't use this class directly from your code. Use {@link ClientSdk.binaryOptions} static method instead.
 *
 * Binary options facade class.
 */
export class BinaryOptions {
    /**
     * Actives current state.
     * @private
     */
    actives = new Map();
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * Timer for periodical actives list update.
     * @private
     */
    intervalId;
    /**
     * Creates instance from DTO.
     * @param activesMsg - actives data transfer object.
     * @param wsApiClient - Instance of WebSocket API client.
     * @internal
     * @private
     */
    constructor(activesMsg, wsApiClient) {
        this.wsApiClient = wsApiClient;
        this.updateActives(activesMsg);
    }
    /**
     * Requests information about binary options actives, runs timer for periodical actives list update, puts the information to instance of class BinaryOptions and returns it.
     * @param wsApiClient - Instance of WebSocket API client.
     */
    static async create(wsApiClient) {
        const initializationData = await wsApiClient.doRequest(new CallBinaryOptionsGetInitializationDataV3());
        const binaryOptions = new BinaryOptions(initializationData.binaryActives, wsApiClient);
        binaryOptions.intervalId = setInterval(async () => {
            const response = await wsApiClient.doRequest(new CallBinaryOptionsGetInitializationDataV3());
            binaryOptions.updateActives(response.binaryActives);
        }, 600000);
        return binaryOptions;
    }
    /**
     * Returns list of binary options actives.
     */
    getActives() {
        const list = [];
        for (const [index] of this.actives) {
            list.push(this.actives.get(index));
        }
        return list;
    }
    /**
     * Returns refreshable instance of class BinaryOptionsActive by specified active ID. If active doesn't exist then error will be thrown.
     * @param activeId - Active identification number.
     */
    getActive(activeId) {
        if (!this.actives.has(activeId)) {
            throw new Error(`active with id '${activeId}' is not found`);
        }
        return this.actives.get(activeId);
    }
    /**
     * Makes request for buy binary option.
     * @param instrument - The instrument for which the option is purchased.
     * @param direction - Direction of price change.
     * @param price - The amount of the initial investment.
     * @param balance - The balance from which the initial investment will be written off and upon successful closing of the position, profit will be credited to this balance.
     */
    async buy(instrument, direction, price, balance) {
        const request = new CallBinaryOptionsOpenBinaryOptionV2(instrument.activeId, Math.trunc(instrument.expiredAt.getTime() / 1000), direction, price, balance.id, 100 - instrument.profitCommissionPercent);
        const response = await this.wsApiClient.doRequest(request);
        return new BinaryOptionsOption(response);
    }
    /**
     * Updates actives from DTO.
     * @param activesMsg - Actives data transfer object.
     * @private
     */
    updateActives(activesMsg) {
        for (const index in activesMsg) {
            if (this.actives.has(activesMsg[index].id)) {
                this.actives.get(activesMsg[index].id).update(activesMsg[index]);
            }
            else {
                this.actives.set(activesMsg[index].id, new BinaryOptionsActive(activesMsg[index], this.wsApiClient.currentTime));
            }
            // @todo mark absent actives as deleted.
        }
    }
    /**
     * Closes the instance.
     */
    close() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
        this.actives.forEach((active) => {
            active.close();
        });
    }
}
/**
 * Binary options direction of price change.
 */
export var BinaryOptionsDirection;
(function (BinaryOptionsDirection) {
    /**
     * The decision is that the price will go up.
     */
    BinaryOptionsDirection["Call"] = "call";
    /**
     * The decision is that the price will go down.
     */
    BinaryOptionsDirection["Put"] = "put";
})(BinaryOptionsDirection || (BinaryOptionsDirection = {}));
/**
 * Binary options active refreshable class.
 */
export class BinaryOptionsActive {
    /**
     * Active's identification number.
     */
    id;
    /**
     * How many seconds before expiration time the ability to buyback options for this active will not be allowed.
     */
    buybackDeadtime;
    /**
     * How many seconds before expiration time the ability to purchase options for this active will not be allowed.
     */
    deadtime;
    /**
     * Active's ticker (symbol).
     */
    ticker;
    /**
     * Is buyback available in the active.
     */
    isBuyback;
    /**
     * Is trading suspended on the active.
     */
    isSuspended;
    /**
     * Count of nearest options available for the active.
     */
    optionCount;
    /**
     * List of special instruments available for the active.
     */
    optionSpecial = [];
    /**
     * Expiration times (sizes) available for the active.
     */
    expirationTimes;
    /**
     * The commission is taken from 100% of the profit. Therefore, income percent can be calculated using the following formula: `profitIncomePercent=100-profitCommissionPercent`.
     */
    profitCommissionPercent;
    /**
     * Active's trading schedule.
     */
    schedule = [];
    /**
     * An object with the current time obtained from WebSocket API.
     * @private
     */
    currentTime;
    /**
     * Instruments facade class instance.
     * @private
     */
    instrumentsFacade;
    /**
     * Creates instance from DTO.
     * @param msg - Active's data transfer object.
     * @param currentTime - An object with the current time obtained from WebSocket API.
     * @internal
     * @private
     */
    constructor(msg, currentTime) {
        this.id = msg.id;
        this.deadtime = msg.deadtime;
        this.ticker = msg.ticker;
        this.isBuyback = msg.isBuyback;
        this.isSuspended = msg.isSuspended;
        this.buybackDeadtime = msg.buybackDeadtime;
        this.optionCount = msg.optionCount;
        this.expirationTimes = msg.expirationTimes;
        this.profitCommissionPercent = msg.profitCommission;
        this.schedule = [];
        for (const index in msg.schedule) {
            this.schedule.push(new BinaryOptionsActiveTradingSession(msg.schedule[index][0], msg.schedule[index][1]));
        }
        for (const index in msg.optionSpecial) {
            this.optionSpecial.push(new BinaryOptionsActiveSpecialInstrument(msg.optionSpecial[index]));
        }
        this.currentTime = currentTime;
    }
    /**
     * Returns binary options active's instruments facade.
     */
    async instruments() {
        if (!this.instrumentsFacade) {
            this.instrumentsFacade = await BinaryOptionsActiveInstruments.create(this, this.currentTime);
        }
        return this.instrumentsFacade;
    }
    /**
     * Updates the instance from DTO.
     * @param msg - Active's data transfer object.
     * @private
     */
    update(msg) {
        this.buybackDeadtime = msg.buybackDeadtime;
        this.deadtime = msg.deadtime;
        this.ticker = msg.ticker;
        this.isBuyback = msg.isBuyback;
        this.isSuspended = msg.isSuspended;
        this.expirationTimes = msg.expirationTimes;
        this.optionCount = msg.optionCount;
        this.profitCommissionPercent = msg.profitCommission;
        this.schedule = [];
        for (const index in msg.schedule) {
            this.schedule.push(new BinaryOptionsActiveTradingSession(msg.schedule[index][0], msg.schedule[index][1]));
        }
        this.optionSpecial = [];
        for (const index in msg.optionSpecial) {
            this.optionSpecial.push(new BinaryOptionsActiveSpecialInstrument(msg.optionSpecial[index]));
        }
    }
    /**
     * Checks whether an option on an active can be purchased at a specified time.
     * @param at - Time for which the check is performed.
     */
    canBeBoughtAt(at) {
        if (this.isSuspended) {
            return false;
        }
        const atUnixTimeMilli = at.getTime();
        return this.schedule.findIndex((session) => {
            return session.from.getTime() <= atUnixTimeMilli && session.to.getTime() >= atUnixTimeMilli;
        }) >= 0;
    }
    /**
     * Closes the instance.
     */
    close() {
        if (this.instrumentsFacade) {
            this.instrumentsFacade.close();
        }
    }
}
/**
 * Binary options active trading session class.
 */
export class BinaryOptionsActiveTradingSession {
    /**
     * Start time of trading session.
     */
    from;
    /**
     * End time of trading session.
     */
    to;
    /**
     * Initialises class instance from DTO.
     * @param fromTs - Unix time of session start.
     * @param toTs - Unix time of session end.
     */
    constructor(fromTs, toTs) {
        this.from = new Date(fromTs * 1000);
        this.to = new Date(toTs * 1000);
    }
}
/**
 * Binary options active's instruments facade class. Periodically generates active's instruments based on active's settings.
 */
export class BinaryOptionsActiveInstruments {
    active;
    currentTime;
    /**
     * Instruments current state.
     * @private
     */
    instruments = new Map();
    /**
     * Timer for periodical actives list update.
     * @private
     */
    intervalId;
    /**
     * Creates class instance.
     * @param active - Active.
     * @param currentTime - An object with the current time obtained from WebSocket API.
     * @internal
     * @private
     */
    constructor(active, currentTime) {
        this.active = active;
        this.currentTime = currentTime;
    }
    /**
     * Runs timer for periodical active's instruments list generation, creates instance of this class and returns it.
     * @param active - The active for which instruments are generated.
     * @param currentTime - An object with the current time obtained from WebSocket API.
     */
    static async create(active, currentTime) {
        const instrumentsFacade = new BinaryOptionsActiveInstruments(active, currentTime);
        instrumentsFacade.generateInstruments();
        return instrumentsFacade;
    }
    /**
     * Returns list of instruments available for buy at specified time.
     * @param at - Time for which the check is performed.
     */
    getAvailableForBuyAt(at) {
        const list = [];
        for (const [index] of this.instruments) {
            if (this.instruments.get(index).isAvailableForBuyAt(at)) {
                list.push(this.instruments.get(index));
            }
        }
        return list;
    }
    scheduleNextGeneration() {
        let nextGenerationTime = null;
        const now = this.currentTime.unixMilliTime;
        for (const instrument of this.instruments.values()) {
            const triggerTime = instrument.expiredAt.getTime() - instrument.deadtime * 1000;
            if (triggerTime > now && (nextGenerationTime === null || triggerTime < nextGenerationTime)) {
                nextGenerationTime = triggerTime;
            }
        }
        if (nextGenerationTime !== null) {
            const delay = nextGenerationTime - now;
            this.intervalId = setTimeout(() => this.generateInstruments(), delay);
        }
        else {
            this.intervalId = setTimeout(() => this.generateInstruments(), 30000);
        }
    }
    /**
     * Generates instruments.
     * @private
     */
    generateInstruments() {
        if (!this.active.canBeBoughtAt(new Date(this.currentTime.unixMilliTime))) {
            this.intervalId = setTimeout(() => this.generateInstruments(), 30000);
            return;
        }
        const generatedInstrumentsKeys = [];
        const nowUnixTime = Math.trunc(this.currentTime.unixMilliTime / 1000);
        for (const index in this.active.expirationTimes) {
            const expirationSize = this.active.expirationTimes[index];
            let instrumentExpirationUnixTime = nowUnixTime + expirationSize - nowUnixTime % expirationSize;
            if (instrumentExpirationUnixTime - this.active.deadtime < nowUnixTime) {
                instrumentExpirationUnixTime += expirationSize;
            }
            for (let i = 0; i < this.active.optionCount; i++) {
                const key = `${this.active.id},${expirationSize},${instrumentExpirationUnixTime}`;
                generatedInstrumentsKeys.push(key);
                if (!this.instruments.has(key)) {
                    this.instruments.set(key, new BinaryOptionsActiveInstrument(this.active.id, expirationSize, new Date(instrumentExpirationUnixTime * 1000), this.active.deadtime, this.active.profitCommissionPercent));
                }
                this.instruments.get(key).update(this.active.deadtime);
                instrumentExpirationUnixTime += expirationSize;
            }
        }
        for (const index in this.active.optionSpecial) {
            const specialInstrument = this.active.optionSpecial[index];
            if (!specialInstrument.isEnabled) {
                continue;
            }
            const expirationSize = specialInstrument.title;
            const key = `${this.active.id},${expirationSize},${specialInstrument.expiredAt.toISOString()}`;
            generatedInstrumentsKeys.push(key);
            if (!this.instruments.has(key)) {
                this.instruments.set(key, new BinaryOptionsActiveInstrument(this.active.id, expirationSize, specialInstrument.expiredAt, this.active.deadtime, this.active.profitCommissionPercent));
            }
            this.instruments.get(key).update(this.active.deadtime);
        }
        for (const index in this.instruments) {
            if (!generatedInstrumentsKeys.includes(index)) {
                this.instruments.delete(index);
            }
        }
        this.scheduleNextGeneration();
    }
    /**
     * Closes the instance.
     */
    close() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }
}
/**
 * Binary options active's instrument refreshable class.
 */
export class BinaryOptionsActiveInstrument {
    activeId;
    expirationSize;
    expiredAt;
    deadtime;
    profitCommissionPercent;
    /**
     * Creates instance of the class.
     * @param activeId - Instrument's active ID.
     * @param expirationSize - Instrument's expiration size.
     * @param expiredAt - The time when the instrument will be expired.
     * @param deadtime - How many seconds before expiration time the ability to purchase options for this instrument will not be allowed.
     * @param profitCommissionPercent - The commission is taken from 100% of the profit. Therefore, income percent can be calculated using the following formula: `profitIncomePercent=100-profitCommissionPercent`.
     * @internal
     * @private
     */
    constructor(activeId, expirationSize, expiredAt, deadtime, profitCommissionPercent) {
        this.activeId = activeId;
        this.expirationSize = expirationSize;
        this.expiredAt = expiredAt;
        this.deadtime = deadtime;
        this.profitCommissionPercent = profitCommissionPercent;
    }
    /**
     * Checks availability for buy option at specified time.
     * @param at - Time for which the check is performed.
     */
    isAvailableForBuyAt(at) {
        return this.purchaseEndTime().getTime() > at.getTime();
    }
    /**
     * Returns the time until which it is possible to open trades that will fall into the current expiration.
     * @returns {Date}
     */
    purchaseEndTime() {
        return new Date(this.expiredAt.getTime() - this.deadtime * 1000);
    }
    /**
     * Returns the remaining duration in milliseconds for which it is possible to purchase options.
     * @param {Date} currentTime - The current time.
     * @returns {number} - The remaining duration in milliseconds.
     */
    durationRemainingForPurchase(currentTime) {
        return this.purchaseEndTime().getTime() - currentTime.getTime();
    }
    /**
     * Updates the instance from DTO.
     * @param deadtime - How many seconds before expiration time the ability to purchase options for this instrument will not be allowed.
     * @private
     */
    update(deadtime) {
        this.deadtime = deadtime;
    }
}
/**
 * Binary options active's special instrument class.
 */
export class BinaryOptionsActiveSpecialInstrument {
    /**
     * Instrument's title.
     */
    title;
    /**
     * Is instrument allowed to trade.
     */
    isEnabled;
    /**
     * Instrument's expiration time.
     */
    expiredAt;
    /**
     * Creates instance from DTO.
     * @param msg - Instrument's data transfer object.
     * @internal
     * @private
     */
    constructor(msg) {
        this.title = msg.title;
        this.isEnabled = msg.enabled;
        this.expiredAt = new Date(msg.expiredAt * 1000);
    }
}
/**
 * Binary options option order class.
 */
export class BinaryOptionsOption {
    /**
     * Option's ID.
     */
    id;
    /**
     * Option's active ID.
     */
    activeId;
    /**
     * Option's price direction.
     */
    direction;
    /**
     * Option's expiration time.
     */
    expiredAt;
    /**
     * Option's amount of the initial investment.
     */
    price;
    /**
     * Option's profit income percent.
     */
    profitIncomePercent;
    /**
     * The time when the option was purchased.
     */
    openedAt;
    /**
     * The {@link CurrentQuote.value value} of the quote at which the option was purchased.
     */
    openQuoteValue;
    /**
     * Create instance from DTO.
     * @param msg - Option's data transfer object.
     * @internal
     * @private
     */
    constructor(msg) {
        this.id = msg.id;
        this.activeId = msg.activeId;
        this.direction = msg.direction;
        this.expiredAt = new Date(msg.expired * 1000);
        this.price = msg.price;
        this.profitIncomePercent = msg.profitIncome;
        this.openedAt = new Date(msg.timeRate * 1000);
        this.openQuoteValue = msg.value;
    }
}
/**
 * Don't use this class directly from your code. Use {@link ClientSdk.digitalOptions} static method instead.
 *
 * Digital options facade class.
 */
export class DigitalOptions {
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * Underlyings current state.
     * @private
     */
    underlyings = new Map();
    /**
     * Creates instance from DTO.
     * @param underlyingList - Underlyings data transfer object.
     * @param wsApiClient - Instance of WebSocket API client.
     * @internal
     * @private
     */
    constructor(underlyingList, wsApiClient) {
        this.wsApiClient = wsApiClient;
        for (const index in underlyingList.underlying) {
            const underlying = underlyingList.underlying[index];
            this.underlyings.set(underlying.activeId, new DigitalOptionsUnderlying(underlying, wsApiClient));
        }
    }
    /**
     * Subscribes on underlyings updates, requests current state of underlyings, puts the state into this class instance and returns it.
     * @param wsApiClient - Instance of WebSocket API client.
     */
    static async create(wsApiClient) {
        const request = new SubscribeDigitalOptionInstrumentsUnderlyingListChangedV3();
        await wsApiClient.subscribe(request, (event) => {
            if (event.type !== 'digital-option') {
                return;
            }
            digitalOptionsFacade.updateUnderlyings(event);
        });
        const underlyingList = await wsApiClient.doRequest(new CallDigitalOptionInstrumentsGetUnderlyingListV3(true));
        const digitalOptionsFacade = new DigitalOptions(underlyingList, wsApiClient);
        return digitalOptionsFacade;
    }
    /**
     * Returns list of underlyings available for buy at specified time.
     * @param at - Time for which the check is performed.
     */
    getUnderlyingsAvailableForTradingAt(at) {
        const list = [];
        for (const [activeId] of this.underlyings) {
            if (this.underlyings.get(activeId).isAvailableForTradingAt(at)) {
                list.push(this.underlyings.get(activeId));
            }
        }
        return list;
    }
    /**
     * Makes request for buy digital option.
     * @param instrument - The instrument for which the option is purchased.
     * @param strikePrice - The strike price by which the option is purchased. Can be digit number or string 'SPT'. SPT is a spot strike that is always equal to the {@link CurrentQuote.value value} of the current underlying quote.
     * @param direction - Direction of price change.
     * @param amount - The amount of the initial investment.
     * @param balance - The balance from which the initial investment will be written off and upon successful closing of the position, profit will be credited to this balance.
     */
    async buy(instrument, strikePrice, direction, amount, balance) {
        const strike = instrument.getStrikeByPriceAndDirection(strikePrice, direction);
        const request = new CallDigitalOptionsPlaceDigitalOptionV3(instrument.assetId, strike.symbol, instrument.index, amount, balance.id);
        const response = await this.wsApiClient.doRequest(request);
        return new DigitalOptionsOrder(response);
    }
    /**
     * Shortcut for buy option on spot strike.
     * @param instrument - The instrument for which the option is purchased.
     * @param direction - Direction of price change.
     * @param amount - The amount of the initial investment.
     * @param balance - The balance from which the initial investment will be written off and upon successful closing of the position, profit will be credited to this balance.     */
    buySpotStrike(instrument, direction, amount, balance) {
        return this.buy(instrument, 'SPT', direction, amount, balance);
    }
    /**
     * Updates instance from DTO.
     * @param msg - Underlyings data transfer object.
     * @private
     */
    updateUnderlyings(msg) {
        for (const index in msg.underlying) {
            const underlying = msg.underlying[index];
            if (this.underlyings.has(underlying.activeId)) {
                this.underlyings.get(underlying.activeId).update(underlying);
            }
            else {
                this.underlyings.set(underlying.activeId, new DigitalOptionsUnderlying(underlying, this.wsApiClient));
            }
        }
    }
    /**
     * Closes the instance.
     */
    close() {
        this.underlyings.forEach((underlying) => {
            underlying.close();
        });
    }
}
/**
 * Digital options direction of price change.
 */
export var DigitalOptionsDirection;
(function (DigitalOptionsDirection) {
    /**
     * The decision is that the price will go up.
     */
    DigitalOptionsDirection["Call"] = "call";
    /**
     * The decision is that the price will go down.
     */
    DigitalOptionsDirection["Put"] = "put";
})(DigitalOptionsDirection || (DigitalOptionsDirection = {}));
/**
 * Margin direction.
 */
export var MarginDirection;
(function (MarginDirection) {
    MarginDirection["Buy"] = "buy";
    MarginDirection["Sell"] = "sell";
})(MarginDirection || (MarginDirection = {}));
/**
 * Digital options underlying refreshable class.
 */
export class DigitalOptionsUnderlying {
    /**
     * Underlying active ID.
     */
    activeId;
    /**
     * Is trading suspended on the underlying.
     */
    isSuspended;
    /**
     * Underlying name (ticker/symbol).
     */
    name;
    /**
     * Underlying trading schedule.
     */
    schedule;
    /**
     * Instruments facade class instance.
     * @private
     */
    instrumentsFacade;
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * Creates instance from DTO.
     * @param msg - Underlying data transfer object.
     * @param wsApiClient - Instance of WebSocket API client.
     * @internal
     * @private
     */
    constructor(msg, wsApiClient) {
        this.activeId = msg.activeId;
        this.isSuspended = msg.isSuspended;
        this.name = msg.name;
        this.wsApiClient = wsApiClient;
        this.schedule = [];
        for (const index in msg.schedule) {
            const session = msg.schedule[index];
            this.schedule.push(new DigitalOptionsUnderlyingTradingSession(session.open, session.close));
        }
    }
    /**
     * Checks availability for trading at specified time.
     * @param at - Time for which the check is performed.
     */
    isAvailableForTradingAt(at) {
        if (this.isSuspended) {
            return false;
        }
        const atUnixTimeMilli = at.getTime();
        return this.schedule.findIndex((session) => {
            return session.open.getTime() <= atUnixTimeMilli && session.close.getTime() >= atUnixTimeMilli;
        }) >= 0;
    }
    /**
     * Returns digital options active's instruments facade.
     */
    async instruments() {
        if (!this.instrumentsFacade) {
            this.instrumentsFacade = await DigitalOptionsUnderlyingInstruments.create(this.activeId, this.wsApiClient);
        }
        return this.instrumentsFacade;
    }
    /**
     * Updates the instance from DTO.
     * @param msg - Underlying data transfer object.
     * @private
     */
    update(msg) {
        this.isSuspended = msg.isSuspended;
        this.name = msg.name;
        this.schedule = [];
        for (const index in msg.schedule) {
            const session = msg.schedule[index];
            this.schedule.push(new DigitalOptionsUnderlyingTradingSession(session.open, session.close));
        }
    }
    /**
     * Closes the instance.
     */
    close() {
        if (this.instrumentsFacade) {
            this.instrumentsFacade.close();
        }
    }
}
/**
 * Digital options active trading session class.
 */
export class DigitalOptionsUnderlyingTradingSession {
    /**
     * Start time of trading session.
     */
    open;
    /**
     * End time of trading session.
     */
    close;
    /**
     * Initialises class instance from DTO.
     * @param openTs - Unix time of session start.
     * @param closeTs - Unix time of session end.
     * @internal
     * @private
     */
    constructor(openTs, closeTs) {
        this.open = new Date(openTs * 1000);
        this.close = new Date(closeTs * 1000);
    }
}
/**
 * Digital options underlying instruments facade class.
 */
export class DigitalOptionsUnderlyingInstruments {
    /**
     * Instruments current state.
     * @private
     */
    instruments = new Map();
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * Just private constructor. Use {@link DigitalOptionsUnderlyingInstruments.create create} instead.
     * @internal
     * @private
     */
    constructor() {
    }
    /**
     * Subscribes on underlying instruments updates, requests current state of underlying instruments, puts the state into this class instance and returns it.
     * @param assetId
     * @param wsApiClient
     */
    static async create(assetId, wsApiClient) {
        const instrumentsFacade = new DigitalOptionsUnderlyingInstruments();
        instrumentsFacade.wsApiClient = wsApiClient;
        await wsApiClient.subscribe(new SubscribeDigitalOptionInstrumentsInstrumentGeneratedV3(assetId), (event) => {
            if (event.instrumentType !== 'digital-option' || event.assetId !== assetId) {
                return;
            }
            instrumentsFacade.syncInstrumentFromEvent(event);
        });
        const instruments = await wsApiClient.doRequest(new CallDigitalOptionInstrumentsGetInstrumentsV3(assetId));
        instrumentsFacade.syncInstrumentsFromResponse(instruments);
        return instrumentsFacade;
    }
    /**
     * Returns list of instruments available for buy at specified time.
     * @param at - Time for which the check is performed.
     */
    getAvailableForBuyAt(at) {
        const list = [];
        for (const [index] of this.instruments) {
            if (this.instruments.get(index).isAvailableForBuyAt(at)) {
                list.push(this.instruments.get(index));
            }
        }
        return list;
    }
    /**
     * Updates the instance from DTO.
     * @param msg - Instrument data transfer object.
     * @private
     */
    syncInstrumentFromEvent(msg) {
        if (!this.instruments.has(msg.index)) {
            this.instruments.set(msg.index, new DigitalOptionsUnderlyingInstrument(msg, this.wsApiClient));
        }
        else {
            this.instruments.get(msg.index).sync(msg);
        }
    }
    /**
     * Updates the instance from DTO.
     * @param msg - Instruments data transfer object.
     * @private
     */
    syncInstrumentsFromResponse(msg) {
        const indexes = [];
        for (const index in msg.instruments) {
            const instrument = msg.instruments[index];
            indexes.push(instrument.index);
            this.syncInstrumentFromResponse(instrument);
        }
        for (const [index] of this.instruments) {
            if (!indexes.includes(this.instruments.get(index).index)) {
                this.instruments.delete(index);
            }
        }
    }
    /**
     * Updates the instance from DTO.
     * @param msg - Instrument data transfer object.
     * @private
     */
    syncInstrumentFromResponse(msg) {
        if (!this.instruments.has(msg.index)) {
            this.instruments.set(msg.index, new DigitalOptionsUnderlyingInstrument(msg, this.wsApiClient));
        }
        else {
            this.instruments.get(msg.index).sync(msg);
        }
    }
    /**
     * Closes the instance.
     */
    close() {
        this.instruments.forEach((instrument) => {
            instrument.close();
        });
    }
}
/**
 * Digital options underlying instrument refreshable class.
 */
export class DigitalOptionsUnderlyingInstrument {
    /**
     * Instrument's active ID.
     */
    assetId;
    /**
     * Instrument's deadtime. How many seconds before expiration time the ability to purchase options for this instrument will not be allowed.
     */
    deadtime;
    /**
     * Instrument's expiration time.
     */
    expiration;
    /**
     * Instrument's ID.
     */
    index;
    /**
     * Instrument's type.
     */
    instrumentType;
    /**
     * Instrument's period (expiration size).
     */
    period;
    /**
     * Instrument's strikes.
     */
    strikes = new Map();
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * Timer for periodical actives list update.
     * @private
     */
    intervalId;
    /**
     * Creates instance from DTO.
     * @param msg - Instrument data transfer object.
     * @param wsApiClient
     * @internal
     * @private
     */
    constructor(msg, wsApiClient) {
        this.wsApiClient = wsApiClient;
        this.assetId = msg.assetId;
        this.deadtime = msg.deadtime;
        this.expiration = new Date(msg.expiration * 1000);
        this.index = msg.index;
        this.instrumentType = msg.instrumentType;
        this.period = msg.period;
        for (const index in msg.data) {
            this.strikes.set(msg.data[index].symbol, new DigitalOptionsUnderlyingInstrumentStrike(msg.data[index]));
        }
    }
    /**
     * Checks availability for buy option at specified time.
     * @param at - Time for which the check is performed.
     */
    isAvailableForBuyAt(at) {
        return this.purchaseEndTime().getTime() > at.getTime();
    }
    /**
     * Gets strike with specified price and direction.
     * @param price - Desired strike price.
     * @param direction - Desired strike direction of price change.
     */
    getStrikeByPriceAndDirection(price, direction) {
        for (const strike of this.strikes.values()) {
            if (strike.price === price && strike.direction === direction) {
                return strike;
            }
        }
        throw new Error(`Strike with price '${price}' and direction '${direction}' is not found`);
    }
    /**
     * Returns the time until which it is possible to open trades that will fall into the current expiration.
     * @returns {Date}
     */
    purchaseEndTime() {
        return new Date(this.expiration.getTime() - this.deadtime * 1000);
    }
    /**
     * Subscribes on strikes ask/bid prices updates.
     */
    async subscribeOnStrikesAskBidPrices() {
        const request = new SubscribeTradingSettingsDigitalOptionClientPriceGeneratedV1('digital-option', this.assetId, this.index);
        await this.wsApiClient.subscribe(request, (event) => {
            this.syncAskBidPricesFromEvent(event);
        });
        this.intervalId = setInterval(() => {
            if (this.wsApiClient.currentTime.unixMilliTime >= this.expiration.getTime()) {
                this.wsApiClient.unsubscribe(request);
                clearInterval(this.intervalId);
            }
        }, 1000);
    }
    /**
     * Returns the remaining duration in milliseconds for which it is possible to purchase options.
     * @param {Date} currentTime - The current time.
     * @returns {number} - The remaining duration in milliseconds.
     */
    durationRemainingForPurchase(currentTime) {
        return this.purchaseEndTime().getTime() - currentTime.getTime();
    }
    /**
     * Updates the instance from DTO.
     * @param msg - Instrument data transfer object.
     */
    sync(msg) {
        this.assetId = msg.assetId;
        this.deadtime = msg.deadtime;
        this.expiration = new Date(msg.expiration * 1000);
        this.instrumentType = msg.instrumentType;
        this.period = msg.period;
        this.strikes = new Map();
        for (const index in msg.data) {
            this.strikes.set(msg.data[index].symbol, new DigitalOptionsUnderlyingInstrumentStrike(msg.data[index]));
        }
    }
    syncAskBidPricesFromEvent(msg) {
        msg.prices.map((price) => {
            const callSymbol = this.strikes.get(price.call.symbol);
            if (callSymbol) {
                callSymbol.ask = price.call.ask;
                callSymbol.bid = price.call.bid;
            }
            const putSymbol = this.strikes.get(price.put.symbol);
            if (putSymbol) {
                putSymbol.ask = price.put.ask;
                putSymbol.bid = price.put.bid;
            }
        });
    }
    /**
     * Closes the instance.
     */
    close() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }
}
/**
 * Digital options underlying instrument strike class.
 */
export class DigitalOptionsUnderlyingInstrumentStrike {
    /**
     * Direction of price change.
     */
    direction;
    /**
     * Strike's price. Can be digit number or string 'SPT'. SPT is a spot strike that is always equal to the {@link CurrentQuote.value value} of the current underlying quote.
     */
    price;
    /**
     * Strike's symbol.
     */
    symbol;
    /**
     * Ask price.
     */
    ask;
    /**
     * Bid price.
     */
    bid;
    /**
     * Creates instance from DTO.
     * @param msg - Strike data transfer object.
     * @internal
     * @private
     */
    constructor(msg) {
        this.direction = msg.direction;
        this.price = msg.strike;
        this.symbol = msg.symbol;
    }
}
/**
 * Digital options order (option) class.
 */
export class DigitalOptionsOrder {
    /**
     * Order's ID.
     */
    id;
    /**
     * Creates instance from DTO.
     * @param msg - Order data transfer object.
     * @internal
     * @private
     */
    constructor(msg) {
        this.id = msg.id;
    }
}
/**
 * Margin order class.
 */
export class MarginOrder {
    /**
     * Order's ID.
     */
    id;
    /**
     * Creates instance from DTO.
     * @param msg - Order data transfer object.
     * @internal
     * @private
     */
    constructor(msg) {
        this.id = msg.id;
    }
}
export class MarginForex {
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * Underlyings current state.
     * @private
     */
    underlyings = new Map();
    /**
     * Creates instance from DTO.
     * @param underlyingList - Underlyings data transfer object.
     * @param wsApiClient - Instance of WebSocket API client.
     * @internal
     * @private
     */
    constructor(underlyingList, wsApiClient) {
        this.wsApiClient = wsApiClient;
        for (const index in underlyingList.items) {
            const underlying = underlyingList.items[index];
            this.underlyings.set(underlying.activeId, new MarginUnderlying(underlying, "forex", wsApiClient));
        }
    }
    /**
     * Subscribes on underlyings updates, requests current state of underlyings, puts the state into this class instance and returns it.
     * @param wsApiClient - Instance of WebSocket API client.
     */
    static async create(wsApiClient) {
        const request = new SubscribeMarginInstrumentsUnderlyingListChangedV1("forex");
        await wsApiClient.subscribe(request, (event) => {
            marginForexFacade.updateUnderlyings(event);
        });
        const underlyingList = await wsApiClient.doRequest(new CallMarginInstrumentsGetUnderlyingListV1("forex"));
        const marginForexFacade = new MarginForex(underlyingList, wsApiClient);
        return marginForexFacade;
    }
    /**
     * Makes request for buy margin active.
     * @param instrument - The instrument for which the option is purchased.
     * @param direction - Direction of price change.
     * @param count
     * @param balance - The balance from which the initial investment will be written off and upon successful closing of the position, profit will be credited to this balance.
     * @param stopLoss
     * @param takeProfit
     */
    async buy(instrument, direction, count, balance, stopLoss = null, takeProfit = null) {
        const request = new CallMarginPlaceMarketOrderV1(direction, balance.id, count.toString(), instrument.id, instrument.activeId, instrument.calculateLeverageProfile(balance).toString(), "forex", stopLoss, takeProfit);
        const response = await this.wsApiClient.doRequest(request);
        return new MarginOrder(response);
    }
    /**
     * Makes stop order request for buy margin active.
     * If the stop order price is on the opposite side of the current market price, it will be converted to a limit order.
     * @param instrument
     * @param direction
     * @param count
     * @param balance
     * @param stopPrice
     * @param takeProfit
     * @param stopLoss
     */
    async buyStop(instrument, direction, count, balance, stopPrice, stopLoss = null, takeProfit = null) {
        const request = new CallMarginPlaceStopOrderV1(direction, balance.id, count.toString(), stopPrice.toString(), instrument.id, instrument.activeId, instrument.calculateLeverageProfile(balance).toString(), 'forex', stopLoss, takeProfit);
        const response = await this.wsApiClient.doRequest(request);
        return new MarginOrder(response);
    }
    /**
     * Makes limit order request for buy margin active.
     * If the limit order price is on the opposite side of the current market price, it will be converted to a stop order.
     * @param instrument
     * @param direction
     * @param count
     * @param balance
     * @param limitPrice
     * @param stopLoss
     * @param takeProfit
     */
    async buyLimit(instrument, direction, count, balance, limitPrice, stopLoss = null, takeProfit = null) {
        const request = new CallMarginPlaceLimitOrderV1(direction, balance.id, count.toString(), limitPrice.toString(), instrument.id, instrument.activeId, instrument.calculateLeverageProfile(balance).toString(), 'forex', stopLoss, takeProfit);
        const response = await this.wsApiClient.doRequest(request);
        return new MarginOrder(response);
    }
    /**
     * Returns list of underlyings available for buy at specified time.
     * @param at - Time for which the check is performed.
     */
    getUnderlyingsAvailableForTradingAt(at) {
        const list = [];
        for (const [activeId] of this.underlyings) {
            if (this.underlyings.get(activeId).isAvailableForTradingAt(at)) {
                list.push(this.underlyings.get(activeId));
            }
        }
        return list;
    }
    /**
     * Updates instance from DTO.
     * @param msg - Underlyings data transfer object.
     * @private
     */
    updateUnderlyings(msg) {
        for (const index in msg.items) {
            const underlying = msg.items[index];
            if (this.underlyings.has(underlying.activeId)) {
                this.underlyings.get(underlying.activeId).update(underlying);
            }
            else {
                this.underlyings.set(underlying.activeId, new MarginUnderlying(underlying, "forex", this.wsApiClient));
            }
        }
    }
}
export class MarginCfd {
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * Underlyings current state.
     * @private
     */
    underlyings = new Map();
    /**
     * Creates instance from DTO.
     * @param underlyingList - Underlyings data transfer object.
     * @param wsApiClient - Instance of WebSocket API client.
     * @internal
     * @private
     */
    constructor(underlyingList, wsApiClient) {
        this.wsApiClient = wsApiClient;
        for (const index in underlyingList.items) {
            const underlying = underlyingList.items[index];
            this.underlyings.set(underlying.activeId, new MarginUnderlying(underlying, "cfd", wsApiClient));
        }
    }
    /**
     * Subscribes on underlyings updates, requests current state of underlyings, puts the state into this class instance and returns it.
     * @param wsApiClient - Instance of WebSocket API client.
     */
    static async create(wsApiClient) {
        const request = new SubscribeMarginInstrumentsUnderlyingListChangedV1("cfd");
        await wsApiClient.subscribe(request, (event) => {
            marginForexFacade.updateUnderlyings(event);
        });
        const underlyingList = await wsApiClient.doRequest(new CallMarginInstrumentsGetUnderlyingListV1("cfd"));
        const marginForexFacade = new MarginCfd(underlyingList, wsApiClient);
        return marginForexFacade;
    }
    /**
     * Makes request for buy margin active.
     * @param instrument - The instrument for which the option is purchased.
     * @param direction - Direction of price change.
     * @param count
     * @param balance - The balance from which the initial investment will be written off and upon successful closing of the position, profit will be credited to this balance.
     * @param takeProfit
     * @param stopLoss
     */
    async buy(instrument, direction, count, balance, stopLoss = null, takeProfit = null) {
        const request = new CallMarginPlaceMarketOrderV1(direction, balance.id, count.toString(), instrument.id, instrument.activeId, instrument.calculateLeverageProfile(balance).toString(), "cfd", stopLoss, takeProfit);
        const response = await this.wsApiClient.doRequest(request);
        return new MarginOrder(response);
    }
    /**
     * Makes stop order request for buy margin active.
     * If the stop order price is on the opposite side of the current market price, it will be converted to a limit order.
     * @param instrument
     * @param direction
     * @param count
     * @param balance
     * @param stopPrice
     * @param takeProfit
     * @param stopLoss
     */
    async buyStop(instrument, direction, count, balance, stopPrice, stopLoss = null, takeProfit = null) {
        const request = new CallMarginPlaceStopOrderV1(direction, balance.id, count.toString(), stopPrice.toString(), instrument.id, instrument.activeId, instrument.calculateLeverageProfile(balance).toString(), 'cfd', stopLoss, takeProfit);
        const response = await this.wsApiClient.doRequest(request);
        return new MarginOrder(response);
    }
    /**
     * Makes limit order request for buy margin active.
     * If the limit order price is on the opposite side of the current market price, it will be converted to a stop order.
     * @param instrument
     * @param direction
     * @param count
     * @param balance
     * @param limitPrice
     * @param stopLoss
     * @param takeProfit
     */
    async buyLimit(instrument, direction, count, balance, limitPrice, stopLoss = null, takeProfit = null) {
        const request = new CallMarginPlaceLimitOrderV1(direction, balance.id, count.toString(), limitPrice.toString(), instrument.id, instrument.activeId, instrument.calculateLeverageProfile(balance).toString(), 'cfd', stopLoss, takeProfit);
        const response = await this.wsApiClient.doRequest(request);
        return new MarginOrder(response);
    }
    /**
     * Returns list of underlyings available for buy at specified time.
     * @param at - Time for which the check is performed.
     */
    getUnderlyingsAvailableForTradingAt(at) {
        const list = [];
        for (const [activeId] of this.underlyings) {
            if (this.underlyings.get(activeId).isAvailableForTradingAt(at)) {
                list.push(this.underlyings.get(activeId));
            }
        }
        return list;
    }
    /**
     * Updates instance from DTO.
     * @param msg - Underlyings data transfer object.
     * @private
     */
    updateUnderlyings(msg) {
        for (const index in msg.items) {
            const underlying = msg.items[index];
            if (this.underlyings.has(underlying.activeId)) {
                this.underlyings.get(underlying.activeId).update(underlying);
            }
            else {
                this.underlyings.set(underlying.activeId, new MarginUnderlying(underlying, "cfd", this.wsApiClient));
            }
        }
    }
}
export class MarginCrypto {
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * Underlyings current state.
     * @private
     */
    underlyings = new Map();
    /**
     * Creates instance from DTO.
     * @param underlyingList - Underlyings data transfer object.
     * @param wsApiClient - Instance of WebSocket API client.
     * @internal
     * @private
     */
    constructor(underlyingList, wsApiClient) {
        this.wsApiClient = wsApiClient;
        for (const index in underlyingList.items) {
            const underlying = underlyingList.items[index];
            this.underlyings.set(underlying.activeId, new MarginUnderlying(underlying, "crypto", wsApiClient));
        }
    }
    /**
     * Subscribes on underlyings updates, requests current state of underlyings, puts the state into this class instance and returns it.
     * @param wsApiClient - Instance of WebSocket API client.
     */
    static async create(wsApiClient) {
        const request = new SubscribeMarginInstrumentsUnderlyingListChangedV1("crypto");
        await wsApiClient.subscribe(request, (event) => {
            marginForexFacade.updateUnderlyings(event);
        });
        const underlyingList = await wsApiClient.doRequest(new CallMarginInstrumentsGetUnderlyingListV1("crypto"));
        const marginForexFacade = new MarginCrypto(underlyingList, wsApiClient);
        return marginForexFacade;
    }
    /**
     * Makes request for buy margin active.
     * @param instrument - The instrument for which the option is purchased.
     * @param direction - Direction of price change.
     * @param count
     * @param balance - The balance from which the initial investment will be written off and upon successful closing of the position, profit will be credited to this balance.
     * @param stopLoss
     * @param takeProfit
     */
    async buy(instrument, direction, count, balance, stopLoss = null, takeProfit = null) {
        const request = new CallMarginPlaceMarketOrderV1(direction, balance.id, count.toString(), instrument.id, instrument.activeId, instrument.calculateLeverageProfile(balance).toString(), "crypto", stopLoss, takeProfit);
        const response = await this.wsApiClient.doRequest(request);
        return new MarginOrder(response);
    }
    /**
     * Makes stop order request for buy margin active.
     * If the stop order price is on the opposite side of the current market price, it will be converted to a limit order.
     * @param instrument
     * @param direction
     * @param count
     * @param balance
     * @param stopPrice
     * @param takeProfit
     * @param stopLoss
     */
    async buyStop(instrument, direction, count, balance, stopPrice, stopLoss = null, takeProfit = null) {
        const request = new CallMarginPlaceStopOrderV1(direction, balance.id, count.toString(), stopPrice.toString(), instrument.id, instrument.activeId, instrument.calculateLeverageProfile(balance).toString(), 'crypto', stopLoss, takeProfit);
        const response = await this.wsApiClient.doRequest(request);
        return new MarginOrder(response);
    }
    /**
     * Makes limit order request for buy margin active.
     * If the limit order price is on the opposite side of the current market price, it will be converted to a stop order.
     * @param instrument
     * @param direction
     * @param count
     * @param balance
     * @param limitPrice
     * @param stopLoss
     * @param takeProfit
     */
    async buyLimit(instrument, direction, count, balance, limitPrice, stopLoss = null, takeProfit = null) {
        const request = new CallMarginPlaceLimitOrderV1(direction, balance.id, count.toString(), limitPrice.toString(), instrument.id, instrument.activeId, instrument.calculateLeverageProfile(balance).toString(), 'crypto', stopLoss, takeProfit);
        const response = await this.wsApiClient.doRequest(request);
        return new MarginOrder(response);
    }
    /**
     * Returns list of underlyings available for buy at specified time.
     * @param at - Time for which the check is performed.
     */
    getUnderlyingsAvailableForTradingAt(at) {
        const list = [];
        for (const [activeId] of this.underlyings) {
            if (this.underlyings.get(activeId).isAvailableForTradingAt(at)) {
                list.push(this.underlyings.get(activeId));
            }
        }
        return list;
    }
    /**
     * Updates instance from DTO.
     * @param msg - Underlyings data transfer object.
     * @private
     */
    updateUnderlyings(msg) {
        for (const index in msg.items) {
            const underlying = msg.items[index];
            if (this.underlyings.has(underlying.activeId)) {
                this.underlyings.get(underlying.activeId).update(underlying);
            }
            else {
                this.underlyings.set(underlying.activeId, new MarginUnderlying(underlying, "crypto", this.wsApiClient));
            }
        }
    }
}
export class MarginUnderlying {
    /**
     * Underlying active ID.
     */
    activeId;
    /**
     * Margin instrument type (cfd/crypto/forex).
     * @private
     */
    marginInstrumentType;
    /**
     * Is trading suspended on the underlying.
     */
    isSuspended;
    /**
     * Underlying name (ticker/symbol).
     */
    name;
    /**
     * Underlying trading schedule.
     */
    schedule;
    /**
     * Instruments facade class instance.
     * @private
     */
    instrumentsFacade;
    /**
     * Instance of WebSocket API client.
     * @private
     */
    wsApiClient;
    /**
     * Creates instance from DTO.
     * @param msg - Underlying data transfer object.
     * @param marginInstrumentType
     * @param wsApiClient - Instance of WebSocket API client.
     * @internal
     * @private
     */
    constructor(msg, marginInstrumentType, wsApiClient) {
        this.activeId = msg.activeId;
        this.marginInstrumentType = marginInstrumentType;
        this.isSuspended = msg.isSuspended;
        this.name = msg.name;
        this.wsApiClient = wsApiClient;
        this.schedule = [];
        for (const index in msg.schedule) {
            const session = msg.schedule[index];
            this.schedule.push(new MarginUnderlyingTradingSession(session.open, session.close));
        }
    }
    /**
     * Checks availability for trading at specified time.
     * @param at - Time for which the check is performed.
     */
    isAvailableForTradingAt(at) {
        if (this.isSuspended) {
            return false;
        }
        const atUnixTimeMilli = at.getTime();
        return this.schedule.findIndex((session) => {
            return session.open.getTime() <= atUnixTimeMilli && session.close.getTime() >= atUnixTimeMilli;
        }) >= 0;
    }
    /**
     * Returns margin active's instruments facade.
     */
    async instruments() {
        if (!this.instrumentsFacade) {
            this.instrumentsFacade = await MarginUnderlyingInstruments.create(this.activeId, this.marginInstrumentType, this.wsApiClient);
        }
        return this.instrumentsFacade;
    }
    /**
     * Updates the instance from DTO.
     * @param msg - Underlying data transfer object.
     * @private
     */
    update(msg) {
        this.isSuspended = msg.isSuspended;
        this.name = msg.name;
        this.schedule = [];
        for (const index in msg.schedule) {
            const session = msg.schedule[index];
            this.schedule.push(new MarginUnderlyingTradingSession(session.open, session.close));
        }
    }
}
/**
 * Margin forex active trading session class.
 */
export class MarginUnderlyingTradingSession {
    /**
     * Start time of trading session.
     */
    open;
    /**
     * End time of trading session.
     */
    close;
    /**
     * Initialises class instance from DTO.
     * @param openTs - Unix time of session start.
     * @param closeTs - Unix time of session end.
     * @internal
     * @private
     */
    constructor(openTs, closeTs) {
        this.open = new Date(openTs * 1000);
        this.close = new Date(closeTs * 1000);
    }
}
/**
 * Margin underlying instruments facade class.
 */
export class MarginUnderlyingInstruments {
    /**
     * Instruments current state.
     * @private
     */
    instruments = new Map();
    /**
     * Just private constructor. Use {@link MarginUnderlyingInstruments.create create} instead.
     * @internal
     * @private
     */
    constructor() {
    }
    /**
     * Subscribes on underlying instruments updates, requests current state of underlying instruments, puts the state into this class instance and returns it.
     * @param activeId
     * @param marginInstrumentType
     * @param wsApiClient
     */
    static async create(activeId, marginInstrumentType, wsApiClient) {
        const instrumentsFacade = new MarginUnderlyingInstruments();
        const instruments = await wsApiClient.doRequest(new CallMarginInstrumentsGetInstrumentsListV1(activeId, marginInstrumentType));
        instrumentsFacade.syncInstrumentsFromResponse(instruments);
        return instrumentsFacade;
    }
    /**
     * Returns list of instruments available for buy at specified time.
     * @param at - Time for which the check is performed.
     */
    getAvailableForBuyAt(at) {
        const list = [];
        for (const [index] of this.instruments) {
            if (this.instruments.get(index).isAvailableForBuyAt(at)) {
                list.push(this.instruments.get(index));
            }
        }
        return list;
    }
    /**
     * Updates the instance from DTO.
     * @param msg - Instruments data transfer object.
     * @private
     */
    syncInstrumentsFromResponse(msg) {
        const instrumentIds = [];
        for (const index in msg.items) {
            const instrument = msg.items[index];
            instrumentIds.push(instrument.id);
            this.syncInstrumentFromResponse(instrument);
        }
        for (const [index] of this.instruments) {
            if (!instrumentIds.includes(this.instruments.get(index).id)) {
                this.instruments.delete(index);
            }
        }
    }
    /**
     * Updates the instance from DTO.
     * @param msg - Instrument data transfer object.
     * @private
     */
    syncInstrumentFromResponse(msg) {
        if (!this.instruments.has(msg.id)) {
            this.instruments.set(msg.id, new MarginUnderlyingInstrument(msg));
        }
        else {
            this.instruments.get(msg.id).sync(msg);
        }
    }
}
/**
 * Margin underlying instruments facade class.
 */
export class MarginUnderlyingInstrument {
    /**
     * Instrument ID.
     */
    id;
    /**
     * Active ID of the instrument.
     */
    activeId;
    /**
     * Allow long positions.
     */
    allowLongPosition;
    /**
     * Allow short positions.
     */
    allowShortPosition;
    /**
     * Default leverage for the instrument.
     */
    defaultLeverage;
    /**
     * Leverage profile for the instrument.
     */
    leverageProfile;
    /**
     * Indicates if the instrument is suspended.
     */
    isSuspended;
    /**
     * The minimum amount when buying an asset.
     */
    minQty;
    /**
     * The step of the amount when buying an asset.
     */
    qtyStep;
    /**
     * Active trading schedule.
     */
    tradable;
    /**
     * Dynamic leverage profiles.
     */
    dynamicLeverageProfiles;
    /**
     * Creates instance from DTO.
     * @param msg - Instrument data transfer object.
     * @internal
     * @private
     */
    constructor(msg) {
        this.id = msg.id;
        this.activeId = msg.activeId;
        this.allowLongPosition = msg.allowLongPosition;
        this.allowShortPosition = msg.allowShortPosition;
        this.defaultLeverage = msg.defaultLeverage;
        this.leverageProfile = msg.leverageProfile;
        this.isSuspended = msg.isSuspended;
        this.minQty = parseFloat(msg.minQty);
        this.qtyStep = parseFloat(msg.qtyStep);
        this.tradable = new MarginUnderlyingInstrumentTradable(msg.tradable.from, msg.tradable.to);
        this.dynamicLeverageProfiles = msg.dynamicLeverageProfile;
    }
    /**
     * Checks availability for buy option at specified time.
     * @param at - Time for which the check is performed.
     */
    isAvailableForBuyAt(at) {
        const atUnixTimeMilli = at.getTime();
        return this.tradable.from.getTime() <= atUnixTimeMilli && this.tradable.to.getTime() >= atUnixTimeMilli;
    }
    /**
     * Returns the remaining duration in milliseconds for which it is possible to purchase options.
     * @param {Date} currentTime - The current time.
     * @returns {number} - The remaining duration in milliseconds.
     */
    durationRemainingForPurchase(currentTime) {
        if (!this.isAvailableForBuyAt(currentTime)) {
            return 0;
        }
        return this.tradable.to.getTime() - currentTime.getTime();
    }
    sync(msg) {
        this.id = msg.id;
        this.activeId = msg.activeId;
        this.allowLongPosition = msg.allowLongPosition;
        this.allowShortPosition = msg.allowShortPosition;
        this.defaultLeverage = msg.defaultLeverage;
        this.leverageProfile = msg.leverageProfile;
        this.isSuspended = msg.isSuspended;
        this.minQty = parseFloat(msg.minQty);
        this.qtyStep = parseFloat(msg.qtyStep);
        this.tradable = new MarginUnderlyingInstrumentTradable(msg.tradable.from, msg.tradable.to);
        this.dynamicLeverageProfiles = msg.dynamicLeverageProfile;
    }
    calculateLeverageProfile(balance) {
        if (!this.dynamicLeverageProfiles) {
            return this.defaultLeverage;
        }
        if (!balance.equityUsd) {
            return this.defaultLeverage;
        }
        if (this.dynamicLeverageProfiles.length === 1) {
            return this.dynamicLeverageProfiles[0].leverage;
        }
        let leverage = this.defaultLeverage;
        for (const index in this.dynamicLeverageProfiles) {
            const profile = this.dynamicLeverageProfiles[index];
            if (balance.equityUsd < profile.equity) {
                return leverage;
            }
            else {
                leverage = profile.leverage;
            }
        }
        return this.dynamicLeverageProfiles[this.dynamicLeverageProfiles.length - 1].leverage;
    }
}
class MarginUnderlyingInstrumentTradable {
    /**
     * Start time of trading session.
     */
    from;
    /**
     * End time of trading session.
     */
    to;
    /**
     * Initialises class instance from DTO.
     * @param fromTs - Unix time of session start.
     * @param toTs - Unix time of session end.
     */
    constructor(fromTs, toTs) {
        this.from = new Date(fromTs * 1000);
        this.to = new Date(toTs * 1000);
    }
}
// Common classes
/**
 * Observable class.
 * @ignore
 * @internal
 */
class Observable {
    observers = [];
    subscribe(func) {
        this.observers.push(func);
    }
    unsubscribe(func) {
        this.observers = this.observers.filter((observer) => observer !== func);
    }
    notify(data) {
        this.observers.forEach((observer) => observer(data));
    }
}
/**
 * HttpApiClient class.
 * @ignore
 * @internal
 */
class HttpApiClient {
    apiUrl;
    constructor(apiUrl) {
        this.apiUrl = apiUrl;
    }
    doRequest(request) {
        return new Promise((resolve, reject) => {
            const options = {
                method: request.method(),
                headers: {
                    'Content-Type': 'application/json',
                    'User-Agent': 'quadcode-client-sdk-js/1.1.0'
                },
                body: JSON.stringify(request.messageBody())
            };
            fetch(`${this.apiUrl}${request.path()}`, options)
                .then(async (response) => {
                if (!response.ok) {
                    if (response.status >= 400 && response.status < 500) {
                        const data = await response.json();
                        resolve(request.createResponse(response.status, data));
                    }
                    reject(new Error(`HTTP error: ${response.status}`));
                }
                const data = await response.json();
                resolve(request.createResponse(response.status, data));
            })
                .catch((error) => {
                reject(error);
            });
        });
    }
}
// WS API client
/**
 * WebSocket API client class.
 * @ignore
 * @internal
 */
class WsApiClient {
    currentTime;
    apiUrl;
    platformId;
    authMethod;
    isBrowser = typeof window !== 'undefined';
    initialReconnectTimeout = 100;
    reconnectMultiplier = 2;
    maxReconnectTimeout = 10000;
    reconnectTimeout = 100;
    disconnecting = false;
    connection;
    lastRequestId = 0;
    requests = new Map();
    subscriptions = new Map();
    constructor(apiUrl, platformId, authMethod) {
        this.currentTime = new WsApiClientCurrentTime(new Date().getTime());
        this.apiUrl = apiUrl;
        this.platformId = platformId;
        this.authMethod = authMethod;
    }
    connect() {
        if (!this.isBrowser) {
            this.connection = new WebSocket(this.apiUrl, {
                headers: {
                    'cookie': `platform=${this.platformId}`,
                    'user-agent': 'quadcode-client-sdk-js/1.3.1'
                }
            });
        }
        else {
            document.cookie = `platform=${this.platformId};`;
            this.connection = new WebSocket(this.apiUrl);
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error ignore
        this.connection.onmessage = ({ data }) => {
            const frame = JSON.parse(data);
            if (frame.request_id) {
                if (this.requests.has(frame.request_id)) {
                    const requestMetaData = this.requests.get(frame.request_id);
                    if (frame.status >= 4000) {
                        requestMetaData.reject(new Error(`request is failed with status ${frame.status} and message: ${frame.msg.message}`));
                        return;
                    }
                    if (frame.name === 'result' && !requestMetaData.request.resultOnly()) {
                        const result = new Result(frame.msg);
                        if (!result.success) {
                            requestMetaData.reject(`request result is not successful`);
                        }
                        return;
                    }
                    try {
                        const response = requestMetaData.request.createResponse(frame.msg);
                        requestMetaData.resolve(response);
                    }
                    catch (e) {
                        requestMetaData.reject(e);
                    }
                    finally {
                        this.requests.delete(frame.request_id);
                    }
                }
            }
            else if (frame.microserviceName && frame.name) {
                const subscriptionKey = `${frame.microserviceName},${frame.name}`;
                if (this.subscriptions.has(subscriptionKey)) {
                    const subscriptions = this.subscriptions.get(subscriptionKey);
                    for (const index in subscriptions) {
                        const subscriptionMetaData = subscriptions[index];
                        subscriptionMetaData.callback(subscriptionMetaData.request.createEvent(frame.msg));
                    }
                }
            }
            else if (frame.name && frame.name === 'timeSync') {
                this.currentTime.unixMilliTime = frame.msg;
            }
            else if (frame.name && frame.name === 'authenticated' && frame.msg === false) {
                for (const [, requestMetaData] of this.requests) {
                    if (requestMetaData.request instanceof Authenticate) {
                        requestMetaData.reject(new Error('authentication is failed'));
                    }
                }
            }
        };
        return new Promise((resolve, reject) => {
            this.connection.onopen = async () => {
                try {
                    const isSuccessful = await this.authMethod.authenticateWsApiClient(this);
                    if (!isSuccessful) {
                        this.disconnect();
                        return reject(new Error('authentication is failed'));
                    }
                    const setOptionsResponse = await this.doRequest(new SetOptions(true));
                    if (!setOptionsResponse.success) {
                        this.disconnect();
                        return reject(new Error('setOptions operation is failed'));
                    }
                    // BEGIN_EXCLUDE
                    try {
                        const response = await this.doRequest(new CallGetFeaturesV2());
                        for (const feature of response.features) {
                            if (feature.name === 'client-sdk' && feature.status === 'disabled') {
                                return reject(new Error('platform does not support'));
                            }
                        }
                    }
                    catch (error) {
                        // it's okay
                    }
                    // END_EXCLUDE
                    this.connection.onclose = () => {
                        this.reconnect();
                    };
                    this.connection.onerror = () => {
                        this.reconnect();
                    };
                    return resolve();
                }
                catch (e) {
                    return reject(e);
                }
            };
        });
    }
    disconnect() {
        this.disconnecting = true;
        if (this.connection) {
            if (!this.isBrowser) {
                this.connection.terminate();
            }
            else {
                this.connection.close();
            }
        }
        this.connection = undefined;
        this.lastRequestId = 0;
        this.requests.clear();
        this.subscriptions.clear();
    }
    reconnect() {
        if (this.disconnecting) {
            return;
        }
        const attemptReconnect = () => {
            this.connect().then(() => {
                this.resubscribeAll().then(() => {
                    this.reconnectTimeout = this.initialReconnectTimeout;
                });
            }).catch(() => {
                this.reconnectTimeout = Math.min(this.reconnectTimeout * this.reconnectMultiplier, this.maxReconnectTimeout) + this.getJitter();
                setTimeout(attemptReconnect, this.reconnectTimeout);
            });
        };
        setTimeout(attemptReconnect, this.reconnectTimeout);
    }
    getJitter() {
        return Math.floor(Math.random() * 1000);
    }
    doRequest(request) {
        const requestId = (++this.lastRequestId).toString();
        this.connection.send(JSON.stringify({
            name: request.messageName(),
            request_id: requestId,
            msg: request.messageBody()
        }));
        return new Promise((resolve, reject) => {
            this.requests.set(requestId, new RequestMetaData(request, resolve, reject));
        });
    }
    resubscribeAll() {
        return new Promise((resolve, reject) => {
            const promises = [];
            if (this.subscriptions.size > 0) {
                for (const [, value] of this.subscriptions) {
                    for (const index in value) {
                        const subscriptionMetaData = value[index];
                        promises.push(this.doRequest(new SubscribeMessage(subscriptionMetaData.request.messageBody())));
                    }
                }
            }
            Promise.all(promises).then(resolve).catch(reject);
        });
    }
    subscribe(request, callback) {
        return new Promise((resolve, reject) => {
            const subscriptionKey = `${request.eventMicroserviceName()},${request.eventName()}`;
            if (!this.subscriptions.has(subscriptionKey)) {
                this.subscriptions.set(subscriptionKey, []);
            }
            this.subscriptions.get(subscriptionKey).push(new SubscriptionMetaData(request, callback));
            this.doRequest(new SubscribeMessage(request.messageBody())).then(resolve).catch(reject);
        });
    }
    unsubscribe(request) {
        return new Promise((resolve, reject) => {
            const subscriptionKey = `${request.eventMicroserviceName()},${request.eventName()}`;
            if (this.subscriptions.has(subscriptionKey)) {
                const subscriptions = this.subscriptions.get(subscriptionKey);
                for (const index in subscriptions) {
                    const subscriptionMetaData = subscriptions[index];
                    if (subscriptionMetaData.request === request) {
                        subscriptions.splice(parseInt(index), 1);
                        break;
                    }
                }
            }
            this.doRequest(new UnsubscribeMessage(request.messageBody())).then(resolve).catch(reject);
        });
    }
}
class WsApiClientCurrentTime {
    unixMilliTime;
    constructor(unixMilliTime) {
        this.unixMilliTime = unixMilliTime;
    }
}
class RequestMetaData {
    request;
    resolve;
    reject;
    constructor(request, resolve, reject) {
        this.request = request;
        this.resolve = resolve;
        this.reject = reject;
    }
}
class SubscriptionMetaData {
    request;
    callback;
    constructor(request, callback) {
        this.request = request;
        this.callback = callback;
    }
}
// DTO classes
// Inbound messages
class Authenticated {
    isSuccessful;
    constructor(isSuccessful) {
        this.isSuccessful = isSuccessful;
    }
}
class HttpResponse {
    status;
    data;
    constructor(status, data) {
        this.status = status;
        this.data = data;
    }
}
class HttpLoginResponse {
    code;
    ssid;
    constructor(data) {
        this.code = data.code;
        this.ssid = data.ssid;
    }
}
class Result {
    success;
    reason;
    constructor(data) {
        this.success = data.success;
        this.reason = data.reason;
    }
}
class BinaryOptionsOptionV1 {
    id;
    activeId;
    direction;
    expired;
    price;
    profitIncome;
    timeRate;
    type;
    value;
    constructor(data) {
        this.id = data.id;
        this.activeId = data.act;
        this.direction = data.direction;
        this.expired = data.exp;
        this.price = data.price;
        this.profitIncome = data.profit_income;
        this.timeRate = data.time_rate;
        this.type = data.type;
        this.value = data.value;
    }
}
class CoreProfileV1 {
    userId;
    constructor(data) {
        this.userId = data.result.user_id;
    }
}
class DigitalOptionInstrumentsInstrumentGeneratedV3 {
    assetId;
    data = [];
    deadtime;
    expiration;
    index;
    instrumentType;
    period;
    constructor(msg) {
        this.assetId = msg.asset_id;
        for (const index in msg.data) {
            this.data.push(new DigitalOptionInstrumentsInstrumentGeneratedV3DataItem(msg.data[index]));
        }
        this.deadtime = msg.deadtime;
        this.expiration = msg.expiration;
        this.index = msg.index;
        this.instrumentType = msg.instrument_type;
        this.period = msg.period;
    }
}
class DigitalOptionInstrumentsInstrumentGeneratedV3DataItem {
    direction;
    strike;
    symbol;
    constructor(msg) {
        this.direction = msg.direction;
        this.strike = msg.strike;
        this.symbol = msg.symbol;
    }
}
class DigitalOptionInstrumentsInstrumentsV3 {
    instruments = [];
    constructor(data) {
        for (const index in data.instruments) {
            const instrument = data.instruments[index];
            this.instruments.push(new DigitalOptionInstrumentsInstrumentsV3Instrument(instrument));
        }
    }
}
class DigitalOptionInstrumentsInstrumentsV3Instrument {
    assetId;
    data = [];
    deadtime;
    expiration;
    index;
    instrumentType;
    period;
    constructor(msg) {
        this.assetId = msg.asset_id;
        for (const index in msg.data) {
            this.data.push(new DigitalOptionInstrumentsInstrumentsV3InstrumentDataItem(msg.data[index]));
        }
        this.deadtime = msg.deadtime;
        this.expiration = msg.expiration;
        this.index = msg.index;
        this.instrumentType = msg.instrument_type;
        this.period = msg.period;
    }
}
class DigitalOptionInstrumentsInstrumentsV3InstrumentDataItem {
    direction;
    strike;
    symbol;
    constructor(msg) {
        this.direction = msg.direction;
        this.strike = msg.strike;
        this.symbol = msg.symbol;
    }
}
class DigitalOptionInstrumentsUnderlyingListChangedV3 {
    type;
    underlying = [];
    constructor(data) {
        this.type = data.type;
        for (const index in data.underlying) {
            const underlying = data.underlying[index];
            this.underlying.push(new DigitalOptionInstrumentsUnderlyingListChangedV3Underlying(underlying.active_id, underlying.is_suspended, underlying.name, underlying.schedule));
        }
    }
}
class DigitalOptionInstrumentsUnderlyingListChangedV3Underlying {
    activeId;
    isSuspended;
    name;
    schedule;
    constructor(activeId, isSuspended, name, schedule) {
        this.activeId = activeId;
        this.isSuspended = isSuspended;
        this.name = name;
        this.schedule = schedule;
    }
}
class DigitalOptionInstrumentsUnderlyingListV3 {
    type;
    underlying = [];
    constructor(data) {
        this.type = data.type;
        for (const index in data.underlying) {
            const underlying = data.underlying[index];
            this.underlying.push(new DigitalOptionInstrumentsUnderlyingListV3Underlying(underlying.active_id, underlying.is_suspended, underlying.name, underlying.schedule));
        }
    }
}
class DigitalOptionInstrumentsUnderlyingListV3Underlying {
    activeId;
    isSuspended;
    name;
    schedule;
    constructor(activeId, isSuspended, name, schedule) {
        this.activeId = activeId;
        this.isSuspended = isSuspended;
        this.name = name;
        this.schedule = schedule;
    }
}
class DigitalOptionPlacedV3 {
    id;
    constructor(data) {
        this.id = data.id;
    }
}
class MarginOrderPlacedV1 {
    id;
    constructor(data) {
        this.id = data.id;
    }
}
class InitializationDataV3 {
    binaryActives = [];
    blitzActives = [];
    turboActives = [];
    constructor(msg) {
        for (const index in msg.binary.actives) {
            this.binaryActives.push(new InitializationDataV3BinaryActive(msg.binary.actives[index]));
        }
        for (const index in msg.blitz.actives) {
            this.blitzActives.push(new InitializationDataV3BlitzActive(msg.blitz.actives[index]));
        }
        for (const index in msg.turbo.actives) {
            this.turboActives.push(new InitializationDataV3TurboActive(msg.turbo.actives[index]));
        }
    }
}
class InitializationDataV3BlitzActive {
    id;
    ticker;
    isSuspended;
    expirationTimes;
    profitCommission;
    schedule = [];
    constructor(data) {
        this.id = data.id;
        this.ticker = data.ticker;
        this.isSuspended = data.is_suspended;
        this.expirationTimes = data.option.expiration_times;
        this.profitCommission = data.option.profit.commission;
        this.schedule = data.schedule;
    }
}
class InitializationDataV3TurboActive {
    id;
    buybackDeadtime;
    deadtime;
    ticker;
    isBuyback;
    isSuspended;
    optionCount;
    expirationTimes;
    profitCommission;
    schedule = [];
    constructor(data) {
        this.id = data.id;
        this.buybackDeadtime = data.buyback_deadtime;
        this.deadtime = data.deadtime;
        this.ticker = data.ticker;
        this.schedule = data.schedule;
        this.isBuyback = data.is_buyback;
        this.isSuspended = data.is_suspended;
        this.optionCount = data.option.count;
        this.expirationTimes = data.option.expiration_times;
        this.profitCommission = data.option.profit.commission;
    }
}
class InitializationDataV3BinaryActive {
    id;
    buybackDeadtime;
    deadtime;
    ticker;
    isBuyback;
    isSuspended;
    optionCount;
    optionSpecial = [];
    expirationTimes;
    profitCommission;
    schedule = [];
    constructor(data) {
        this.id = data.id;
        this.buybackDeadtime = data.buyback_deadtime;
        this.deadtime = data.deadtime;
        this.ticker = data.ticker;
        this.isBuyback = data.is_buyback;
        this.isSuspended = data.is_suspended;
        this.optionCount = data.option.count;
        this.expirationTimes = data.option.expiration_times;
        this.profitCommission = data.option.profit.commission;
        this.schedule = data.schedule;
        for (const expiredAt in data.option.special) {
            this.optionSpecial.push(new InitializationDataV3BinaryActiveSpecialInstrument(parseInt(expiredAt), data.option.special[expiredAt]));
        }
    }
}
class InitializationDataV3BinaryActiveSpecialInstrument {
    title;
    enabled;
    expiredAt;
    constructor(expiredAt, msg) {
        this.title = msg.title;
        this.enabled = msg.enabled;
        this.expiredAt = expiredAt;
    }
}
class BalancesBalanceChangedV1 {
    id;
    type;
    amount;
    bonusAmount;
    currency;
    userId;
    constructor(data) {
        this.id = data.current_balance.id;
        this.type = data.current_balance.type;
        this.amount = data.current_balance.amount;
        this.bonusAmount = data.current_balance.bonus_amount;
        this.currency = data.current_balance.currency;
        this.userId = data.user_id;
    }
}
class DigitalOptionClientPriceGeneratedV1 {
    instrumentIndex;
    assetId;
    digitalOptionTradingGroupId;
    quoteTime;
    prices = [];
    constructor(data) {
        this.instrumentIndex = data.instrument_index;
        this.assetId = data.asset_id;
        this.digitalOptionTradingGroupId = data.digital_option_trading_group_id;
        this.quoteTime = data.quote_time;
        for (const index in data.prices) {
            this.prices.push(new DigitalOptionClientPriceGeneratedV1Price(data.prices[index]));
        }
    }
}
class DigitalOptionClientPriceGeneratedV1Price {
    strike;
    call;
    put;
    constructor(data) {
        this.strike = data.strike;
        this.call = new DigitalOptionClientPriceGeneratedV1CallOrPutPrice(data.call);
        this.put = new DigitalOptionClientPriceGeneratedV1CallOrPutPrice(data.put);
    }
}
class DigitalOptionClientPriceGeneratedV1CallOrPutPrice {
    symbol;
    ask;
    bid;
    constructor(data) {
        this.symbol = data.symbol;
        this.ask = data.ask;
        this.bid = data.bid;
    }
}
class BalancesAvailableBalancesV1 {
    items = [];
    constructor(balances) {
        for (const index in balances) {
            this.items.push(new BalancesAvailableBalancesV1Balance(balances[index]));
        }
    }
}
class BalancesAvailableBalancesV1Balance {
    id;
    type;
    amount;
    bonusAmount;
    currency;
    userId;
    isMargin;
    constructor(data) {
        this.id = data.id;
        this.type = data.type;
        this.amount = data.amount;
        this.bonusAmount = data.bonus_amount;
        this.currency = data.currency;
        this.userId = data.user_id;
        this.isMargin = data.is_marginal;
    }
}
class PortfolioPositionChangedV3 {
    activeId;
    closeProfit;
    closeQuote;
    closeReason;
    closeTime;
    expectedProfit;
    externalId;
    internalId;
    instrumentType;
    invest;
    openQuote;
    openTime;
    pnl;
    pnlRealized;
    quoteTimestamp;
    status;
    userId;
    userBalanceId;
    version;
    direction;
    expirationTime;
    orderIds;
    constructor(data) {
        this.activeId = data.active_id;
        this.closeProfit = data.close_profit;
        this.closeQuote = data.close_quote;
        this.closeReason = data.close_reason;
        this.closeTime = data.close_time;
        this.expectedProfit = data.expected_profit;
        this.instrumentType = data.instrument_type;
        this.externalId = data.external_id;
        this.internalId = data.id;
        this.invest = data.invest;
        this.openQuote = data.open_quote;
        this.openTime = data.open_time;
        this.pnl = data.pnl;
        this.pnlRealized = data.pnl_realized;
        this.quoteTimestamp = data.quote_timestamp;
        this.status = data.status;
        this.userId = data.user_id;
        this.userBalanceId = data.user_balance_id;
        this.version = data.version;
        if (data.raw_event) {
            let order_ids;
            switch (data.instrument_type) {
                case InstrumentType.BinaryOption:
                case InstrumentType.TurboOption:
                case InstrumentType.BlitzOption:
                    order_ids = data.raw_event.binary_options_option_changed1.order_ids;
                    this.direction = data.raw_event.binary_options_option_changed1.direction;
                    if (data.raw_event.binary_options_option_changed1.expiration_time) {
                        this.expirationTime = data.raw_event.binary_options_option_changed1.expiration_time * 1000;
                    }
                    break;
                case InstrumentType.DigitalOption:
                    order_ids = data.raw_event.digital_options_position_changed1.order_ids;
                    this.direction = data.raw_event.digital_options_position_changed1.instrument_dir;
                    if (data.raw_event.digital_options_position_changed1.instrument_expiration) {
                        this.expirationTime = data.raw_event.digital_options_position_changed1.instrument_expiration;
                    }
                    break;
                case InstrumentType.MarginCfd:
                    order_ids = data.raw_event.marginal_cfd_position_changed1.order_ids;
                    break;
                case InstrumentType.MarginForex:
                    order_ids = data.raw_event.marginal_forex_position_changed1.order_ids;
                    break;
                case InstrumentType.MarginCrypto:
                    order_ids = data.raw_event.marginal_crypto_position_changed1.order_ids;
                    break;
            }
            if (order_ids) {
                this.orderIds = order_ids;
            }
            else {
                this.orderIds = [data.external_id];
            }
        }
        else {
            this.orderIds = [data.external_id];
        }
    }
}
class PortfolioPositionsHistoryV2 {
    limit;
    positions = [];
    constructor(data) {
        this.limit = data.limit;
        for (const index in data.positions) {
            this.positions.push(new PortfolioPositionsHistoryV2Position(data.positions[index]));
        }
    }
}
class PortfolioPositionsHistoryV2Position {
    externalId;
    internalId;
    userId;
    userBalanceId;
    activeId;
    instrumentType;
    status;
    openQuote;
    openTime;
    invest;
    closeProfit;
    closeQuote;
    closeReason;
    closeTime;
    pnl;
    pnlRealized;
    pnlNet;
    orderIds;
    constructor(data) {
        this.activeId = data.active_id;
        this.closeProfit = data.close_profit;
        this.closeQuote = data.close_quote;
        this.closeReason = data.close_reason;
        this.closeTime = data.close_time;
        this.externalId = data.external_id;
        this.internalId = data.id;
        this.instrumentType = data.instrument_type;
        this.invest = data.invest;
        this.openQuote = data.open_quote;
        this.openTime = data.open_time;
        this.pnl = data.pnl;
        this.pnlRealized = data.pnl_realized;
        this.pnlNet = data.pnl_net;
        this.status = data.status;
        this.userId = data.user_id;
        this.userBalanceId = data.user_balance_id;
        if (data.raw_event) {
            let order_ids;
            switch (data.instrument_type) {
                case InstrumentType.BinaryOption:
                case InstrumentType.TurboOption:
                case InstrumentType.BlitzOption:
                    order_ids = data.raw_event.binary_options_option_changed1.order_ids;
                    break;
                case InstrumentType.DigitalOption:
                    order_ids = data.raw_event.digital_options_position_changed1.order_ids;
                    break;
                case InstrumentType.MarginCfd:
                    order_ids = data.raw_event.marginal_cfd_position_changed1.order_ids;
                    break;
                case InstrumentType.MarginForex:
                    order_ids = data.raw_event.marginal_forex_position_changed1.order_ids;
                    break;
                case InstrumentType.MarginCrypto:
                    order_ids = data.raw_event.marginal_crypto_position_changed1.order_ids;
                    break;
            }
            if (order_ids) {
                this.orderIds = order_ids;
            }
            else {
                this.orderIds = [data.external_id];
            }
        }
        else {
            this.orderIds = [data.external_id];
        }
    }
}
class PortfolioPositionsV4 {
    limit;
    positions = [];
    total;
    constructor(data) {
        this.limit = data.limit;
        this.total = data.total;
        for (const index in data.positions) {
            this.positions.push(new PortfolioPositionsV4Position(data.positions[index]));
        }
    }
}
class PortfolioPositionsStateV1 {
    positions = [];
    expiresIn;
    userId;
    subscriptionId;
    constructor(data) {
        this.expiresIn = data.expires_in;
        this.userId = data.user_id;
        this.subscriptionId = data.subscription_id;
        for (const index in data.positions) {
            this.positions.push(new PortfolioPositionsStateV1Position(data.positions[index]));
        }
    }
}
class PortfolioPositionsStateV1Position {
    internalId;
    instrumentType;
    sellProfit;
    margin;
    currentPrice;
    quoteTimestamp;
    pnl;
    pnlNet;
    openPrice;
    expectedProfit;
    currencyConversion;
    constructor(data) {
        this.internalId = data.id;
        this.instrumentType = data.instrument_type;
        this.sellProfit = data.sell_profit;
        this.margin = data.margin;
        this.currentPrice = data.current_price;
        this.quoteTimestamp = data.quote_timestamp;
        this.pnl = data.pnl;
        this.pnlNet = data.pnl_net;
        this.openPrice = data.open_price;
        this.expectedProfit = data.expected_profit;
        this.currencyConversion = data.currency_conversion;
    }
}
class PortfolioPositionsV4Position {
    activeId;
    expectedProfit;
    externalId;
    internalId;
    instrumentType;
    invest;
    openQuote;
    openTime;
    pnl;
    quoteTimestamp;
    status;
    userId;
    userBalanceId;
    orderIds;
    expirationTime;
    direction;
    constructor(data) {
        this.activeId = data.active_id;
        this.expectedProfit = data.expected_profit;
        this.externalId = data.external_id;
        this.internalId = data.id;
        this.instrumentType = data.instrument_type;
        this.invest = data.invest;
        this.openQuote = data.open_quote;
        this.openTime = data.open_time;
        this.pnl = data.pnl;
        this.quoteTimestamp = data.quote_timestamp;
        this.status = data.status;
        this.userId = data.user_id;
        this.userBalanceId = data.user_balance_id;
        if (data.raw_event) {
            let order_ids;
            switch (data.instrument_type) {
                case InstrumentType.BinaryOption:
                case InstrumentType.TurboOption:
                case InstrumentType.BlitzOption:
                    order_ids = data.raw_event.binary_options_option_changed1.order_ids;
                    this.direction = data.raw_event.binary_options_option_changed1.direction;
                    if (data.raw_event.binary_options_option_changed1.expiration_time) {
                        this.expirationTime = data.raw_event.binary_options_option_changed1.expiration_time * 1000;
                    }
                    break;
                case InstrumentType.DigitalOption:
                    order_ids = data.raw_event.digital_options_position_changed1.order_ids;
                    this.direction = data.raw_event.digital_options_position_changed1.instrument_dir;
                    if (data.raw_event.digital_options_position_changed1.instrument_expiration) {
                        this.expirationTime = data.raw_event.digital_options_position_changed1.instrument_expiration;
                    }
                    break;
                case InstrumentType.MarginCfd:
                    order_ids = data.raw_event.marginal_cfd_position_changed1.order_ids;
                    break;
                case InstrumentType.MarginForex:
                    order_ids = data.raw_event.marginal_forex_position_changed1.order_ids;
                    break;
                case InstrumentType.MarginCrypto:
                    order_ids = data.raw_event.marginal_crypto_position_changed1.order_ids;
                    break;
            }
            if (order_ids) {
                this.orderIds = order_ids;
            }
            else {
                this.orderIds = [data.external_id];
            }
        }
        else {
            this.orderIds = [data.external_id];
        }
    }
}
class PositionsRawEvent {
    binary_options_option_changed1;
    digital_options_position_changed1;
    marginal_forex_position_changed1;
    marginal_cfd_position_changed1;
    marginal_crypto_position_changed1;
}
class BinaryOptionsRawEventItem {
    order_ids;
    direction;
    expiration_time;
}
class PositionsRawEventItem {
    order_ids;
    instrument_dir;
    instrument_expiration;
}
class PortfolioOrdersV2 {
    orders = [];
    constructor(data) {
        for (const index in data.items) {
            this.orders.push(new PortfolioOrdersV2Order(data.items[index]));
        }
    }
}
class PortfolioOrdersV2Order {
    id;
    instrumentType;
    kind;
    positionId;
    status;
    userId;
    userBalanceId;
    constructor(data) {
        this.instrumentType = data.instrument_type;
        this.kind = data.kind;
        this.positionId = data.position_id;
        this.status = data.status;
        this.userId = data.user_id;
        this.userBalanceId = data.user_balance_id;
        if (data.raw_event) {
            switch (data.instrument_type) {
                case InstrumentType.DigitalOption:
                    this.id = data.raw_event.digital_options_order_changed1.id;
                    break;
                case InstrumentType.MarginCfd:
                    this.id = data.raw_event.marginal_cfd_order_changed1.id;
                    break;
                case InstrumentType.MarginForex:
                    this.id = data.raw_event.marginal_forex_order_changed1.id;
                    break;
                case InstrumentType.MarginCrypto:
                    this.id = data.raw_event.marginal_crypto_order_changed1.id;
                    break;
            }
        }
    }
}
class PortfolioOrderChangedV2 {
    id;
    instrumentType;
    kind;
    positionId;
    status;
    userId;
    userBalanceId;
    constructor(data) {
        this.instrumentType = data.instrument_type;
        this.kind = data.kind;
        this.positionId = data.position_id;
        this.status = data.status;
        this.userId = data.user_id;
        this.userBalanceId = data.user_balance_id;
        if (data.raw_event) {
            switch (data.instrument_type) {
                case InstrumentType.DigitalOption:
                    this.id = data.raw_event.digital_options_order_changed1.id;
                    break;
                case InstrumentType.MarginCfd:
                    this.id = data.raw_event.marginal_cfd_order_changed1.id;
                    break;
                case InstrumentType.MarginForex:
                    this.id = data.raw_event.marginal_forex_order_changed1.id;
                    break;
                case InstrumentType.MarginCrypto:
                    this.id = data.raw_event.marginal_crypto_order_changed1.id;
                    break;
            }
        }
    }
}
class OrdersRawEvent {
    digital_options_order_changed1;
    marginal_forex_order_changed1;
    marginal_cfd_order_changed1;
    marginal_crypto_order_changed1;
}
class OrdersRawEventItem {
    id;
    constructor(data) {
        this.id = data.id;
    }
}
class QuoteGeneratedV2 {
    activeId;
    time;
    ask;
    bid;
    value;
    phase;
    constructor(data) {
        this.activeId = data.active_id;
        this.time = data.time;
        this.ask = data.ask;
        this.bid = data.bid;
        this.value = data.value;
        this.phase = data.phase;
    }
}
// Outbound messages
class HttpLoginRequest {
    login;
    password;
    constructor(login, password) {
        this.login = login;
        this.password = password;
    }
    method() {
        return 'POST';
    }
    path() {
        return '/v2/login';
    }
    messageBody() {
        return {
            identifier: this.login,
            password: this.password
        };
    }
    createResponse(status, data) {
        return new HttpResponse(status, new HttpLoginResponse(data));
    }
}
class Authenticate {
    ssid;
    constructor(ssid) {
        this.ssid = ssid;
    }
    messageName() {
        return 'authenticate';
    }
    messageBody() {
        return {
            ssid: this.ssid,
            protocol: 3,
            session_id: '',
            client_session_id: ''
        };
    }
    resultOnly() {
        return false;
    }
    createResponse(data) {
        return new Authenticated(data);
    }
}
class CallBinaryOptionsOpenBinaryOptionV2 {
    activeId;
    expiredAt;
    direction;
    price;
    userBalanceId;
    profitPercent;
    constructor(activeId, expiredAt, direction, price, userBalanceId, profitPercent) {
        this.activeId = activeId;
        this.expiredAt = expiredAt;
        this.direction = direction;
        this.price = price;
        this.userBalanceId = userBalanceId;
        this.profitPercent = profitPercent;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'binary-options.open-option',
            version: '2.0',
            body: {
                active_id: this.activeId,
                direction: this.direction,
                expired: this.expiredAt,
                option_type_id: 1,
                price: this.price,
                user_balance_id: this.userBalanceId,
                profit_percent: this.profitPercent
            }
        };
    }
    createResponse(data) {
        return new BinaryOptionsOptionV1(data);
    }
    resultOnly() {
        return false;
    }
}
class CallPortfolioSubscribePositions {
    frequency;
    positionIds;
    constructor(frequency, positionIds) {
        this.frequency = frequency;
        this.positionIds = positionIds;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'portfolio.subscribe-positions',
            version: '1.0',
            body: {
                frequency: this.frequency,
                ids: this.positionIds
            }
        };
    }
    createResponse(data) {
        return new Result(data);
    }
    resultOnly() {
        return true;
    }
}
class CallPortfolioGetOrdersV2 {
    userBalanceId;
    constructor(userBalanceId) {
        this.userBalanceId = userBalanceId;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'portfolio.get-orders',
            version: '2.0',
            body: {
                user_balance_id: this.userBalanceId,
                kind: 'deferred'
            }
        };
    }
    createResponse(data) {
        return new PortfolioOrdersV2(data);
    }
    resultOnly() {
        return false;
    }
}
class CallMarginClosePositionV1 {
    marginInstrumentType;
    positionId;
    constructor(marginInstrumentType, positionId) {
        this.marginInstrumentType = marginInstrumentType;
        this.positionId = positionId;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: `marginal-${this.marginInstrumentType}.close-position`,
            version: '1.0',
            body: {
                position_id: this.positionId
            }
        };
    }
    createResponse(data) {
        return new Result(data);
    }
    resultOnly() {
        return true;
    }
}
class CallDigitalOptionsClosePositionV1 {
    positionId;
    constructor(positionId) {
        this.positionId = positionId;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'digital-options.close-position',
            version: '1.0',
            body: {
                position_id: this.positionId
            }
        };
    }
    createResponse(data) {
        return new Result(data);
    }
    resultOnly() {
        return true;
    }
}
class CallBinaryOptionsSellOptionsV3 {
    optionsIds;
    constructor(optionsIds) {
        this.optionsIds = optionsIds;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'binary-options.sell-options',
            version: '3.0',
            body: {
                options_ids: this.optionsIds
            }
        };
    }
    createResponse(data) {
        return new Result(data);
    }
    resultOnly() {
        return true;
    }
}
class CallBinaryOptionsOpenBlitzOptionV2 {
    activeId;
    direction;
    expirationSize;
    price;
    userBalanceId;
    profitPercent;
    constructor(activeId, direction, expirationSize, price, userBalanceId, profitPercent) {
        this.activeId = activeId;
        this.direction = direction;
        this.expirationSize = expirationSize;
        this.price = price;
        this.userBalanceId = userBalanceId;
        this.profitPercent = profitPercent;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'binary-options.open-option',
            version: '2.0',
            body: {
                active_id: this.activeId,
                direction: this.direction,
                expiration_size: this.expirationSize,
                expired: 0,
                option_type_id: 12,
                price: this.price,
                user_balance_id: this.userBalanceId,
                profit_percent: this.profitPercent,
            }
        };
    }
    createResponse(data) {
        return new BinaryOptionsOptionV1(data);
    }
    resultOnly() {
        return false;
    }
}
class CallBinaryOptionsOpenTurboOptionV2 {
    activeId;
    expiredAt;
    direction;
    price;
    userBalanceId;
    profitPercent;
    constructor(activeId, expiredAt, direction, price, userBalanceId, profitPercent) {
        this.activeId = activeId;
        this.expiredAt = expiredAt;
        this.direction = direction;
        this.price = price;
        this.userBalanceId = userBalanceId;
        this.profitPercent = profitPercent;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'binary-options.open-option',
            version: '2.0',
            body: {
                active_id: this.activeId,
                direction: this.direction,
                expired: this.expiredAt,
                option_type_id: 3,
                price: this.price,
                user_balance_id: this.userBalanceId,
                profit_percent: this.profitPercent,
            }
        };
    }
    createResponse(data) {
        return new BinaryOptionsOptionV1(data);
    }
    resultOnly() {
        return false;
    }
}
class CallCoreGetProfileV1 {
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'core.get-profile',
            version: '1.0',
            body: {}
        };
    }
    createResponse(data) {
        return new CoreProfileV1(data);
    }
    resultOnly() {
        return false;
    }
}
class CallInternalBillingResetTrainingBalanceV4 {
    userBalanceId;
    amount;
    constructor(userBalanceId, amount) {
        this.userBalanceId = userBalanceId;
        this.amount = amount;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'internal-billing.reset-training-balance',
            version: '4.0',
            body: {
                user_balance_id: this.userBalanceId,
                amount: this.amount
            }
        };
    }
    createResponse(data) {
        return new Result(data);
    }
    resultOnly() {
        return true;
    }
}
class CallDigitalOptionInstrumentsGetInstrumentsV3 {
    assetId;
    constructor(assetId) {
        this.assetId = assetId;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'digital-option-instruments.get-instruments',
            version: '3.0',
            body: {
                asset_id: this.assetId
            }
        };
    }
    createResponse(data) {
        return new DigitalOptionInstrumentsInstrumentsV3(data);
    }
    resultOnly() {
        return false;
    }
}
class CallDigitalOptionInstrumentsGetUnderlyingListV3 {
    filterSuspended;
    constructor(filterSuspended) {
        this.filterSuspended = filterSuspended;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'digital-option-instruments.get-underlying-list',
            version: '3.0',
            body: {
                filter_suspended: this.filterSuspended
            }
        };
    }
    createResponse(data) {
        return new DigitalOptionInstrumentsUnderlyingListV3(data);
    }
    resultOnly() {
        return false;
    }
}
class CallDigitalOptionsPlaceDigitalOptionV3 {
    assetId;
    instrumentId;
    instrumentIndex;
    amount;
    userBalanceId;
    constructor(assetId, instrumentId, instrumentIndex, amount, userBalanceId) {
        this.assetId = assetId;
        this.instrumentId = instrumentId;
        this.instrumentIndex = instrumentIndex;
        this.amount = amount;
        this.userBalanceId = userBalanceId;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'digital-options.place-digital-option',
            version: '3.0',
            body: {
                amount: this.amount.toString(),
                asset_id: this.assetId,
                instrument_id: this.instrumentId,
                instrument_index: this.instrumentIndex,
                user_balance_id: this.userBalanceId
            }
        };
    }
    createResponse(data) {
        return new DigitalOptionPlacedV3(data);
    }
    resultOnly() {
        return false;
    }
}
class CallBinaryOptionsGetInitializationDataV3 {
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'binary-options.get-initialization-data',
            version: '3.0',
            body: {}
        };
    }
    createResponse(data) {
        return new InitializationDataV3(data);
    }
    resultOnly() {
        return false;
    }
}
class CallBalancesGetAvailableBalancesV1 {
    typesIds;
    constructor(typesIds) {
        this.typesIds = typesIds;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'balances.get-available-balances',
            version: '1.0',
            body: {
                types_ids: this.typesIds
            }
        };
    }
    createResponse(data) {
        return new BalancesAvailableBalancesV1(data);
    }
    resultOnly() {
        return false;
    }
}
class CallPortfolioGetPositionsV4 {
    instrumentTypes;
    limit;
    offset;
    constructor(instrumentTypes, limit, offset) {
        this.instrumentTypes = instrumentTypes;
        this.limit = limit;
        this.offset = offset;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'portfolio.get-positions',
            version: '4.0',
            body: {
                instrument_types: this.instrumentTypes,
                limit: this.limit,
                offset: this.offset
            }
        };
    }
    createResponse(data) {
        return new PortfolioPositionsV4(data);
    }
    resultOnly() {
        return false;
    }
}
class CallPortfolioGetHistoryPositionsV2 {
    instrumentTypes;
    userId;
    end;
    limit;
    offset;
    constructor(data) {
        this.instrumentTypes = data.instrumentTypes;
        this.userId = data.userId;
        this.end = data.end;
        this.limit = data.limit;
        this.offset = data.offset;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'portfolio.get-history-positions',
            version: '2.0',
            body: {
                instrument_types: this.instrumentTypes,
                user_id: this.userId,
                end: this.end,
                limit: this.limit,
                offset: this.offset
            }
        };
    }
    createResponse(data) {
        return new PortfolioPositionsHistoryV2(data);
    }
    resultOnly() {
        return false;
    }
}
class CallQuotesHistoryGetCandlesV2 {
    activeId;
    size;
    from;
    to;
    fromId;
    toId;
    count;
    backoff;
    onlyClosed;
    kind;
    splitNormalization;
    constructor(data) {
        this.activeId = data.activeId;
        this.size = data.size;
        if (data.options) {
            this.from = data.options.from;
            this.to = data.options.to;
            this.fromId = data.options.fromId;
            this.toId = data.options.toId;
            this.count = data.options.count;
            this.backoff = data.options.backoff;
            this.onlyClosed = data.options.onlyClosed;
            this.kind = data.options.kind;
            this.splitNormalization = data.options.splitNormalization;
        }
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'quotes-history.get-candles',
            version: '2.0',
            body: {
                active_id: this.activeId,
                size: this.size,
                from: this.from,
                to: this.to,
                from_id: this.fromId,
                to_id: this.toId,
                count: this.count,
                backoff: this.backoff,
                only_closed: this.onlyClosed,
                kind: this.kind,
                split_normalization: this.splitNormalization,
            }
        };
    }
    createResponse(data) {
        return new QuotesHistoryCandlesV2(data);
    }
    resultOnly() {
        return false;
    }
}
class QuotesHistoryCandlesV2 {
    candles = [];
    constructor(data) {
        for (const index in data.candles) {
            this.candles.push(new Candle(data.candles[index]));
        }
    }
}
class SetOptions {
    sendResults;
    constructor(sendResults) {
        this.sendResults = sendResults;
    }
    messageName() {
        return 'setOptions';
    }
    messageBody() {
        return {
            sendResults: this.sendResults
        };
    }
    resultOnly() {
        return true;
    }
    createResponse(data) {
        return new Result(data);
    }
}
class SubscribePortfolioPositionsStateV1 {
    messageName() {
        return 'subscribeMessage';
    }
    messageBody() {
        return {
            name: `${this.eventMicroserviceName()}.${this.eventName()}`,
            version: '1.0',
        };
    }
    eventMicroserviceName() {
        return 'portfolio';
    }
    eventName() {
        return 'positions-state';
    }
    createEvent(data) {
        return new PortfolioPositionsStateV1(data);
    }
}
class SubscribeDigitalOptionInstrumentsInstrumentGeneratedV3 {
    assetId;
    constructor(assetId) {
        this.assetId = assetId;
    }
    messageName() {
        return 'subscribeMessage';
    }
    messageBody() {
        return {
            name: `${this.eventMicroserviceName()}.${this.eventName()}`,
            version: '3.0',
            params: {
                routingFilters: {
                    asset_id: this.assetId
                }
            }
        };
    }
    eventMicroserviceName() {
        return 'digital-option-instruments';
    }
    eventName() {
        return 'instrument-generated';
    }
    createEvent(data) {
        return new DigitalOptionInstrumentsInstrumentGeneratedV3(data);
    }
}
class SubscribeTradingSettingsDigitalOptionClientPriceGeneratedV1 {
    instrumentType;
    assetId;
    instrumentIndex;
    constructor(instrumentType, assetId, instrumentIndex) {
        this.instrumentType = instrumentType;
        this.assetId = assetId;
        this.instrumentIndex = instrumentIndex;
    }
    messageName() {
        return 'subscribeMessage';
    }
    messageBody() {
        return {
            name: `${this.eventMicroserviceName()}.${this.eventName()}`,
            version: '1.0',
            params: {
                routingFilters: {
                    asset_id: this.assetId,
                    instrument_index: this.instrumentIndex,
                    instrument_type: this.instrumentType
                }
            }
        };
    }
    eventMicroserviceName() {
        return 'trading-settings';
    }
    eventName() {
        return 'digital-option-client-price-generated';
    }
    createEvent(data) {
        return new DigitalOptionClientPriceGeneratedV1(data);
    }
}
class SubscribeDigitalOptionInstrumentsUnderlyingListChangedV3 {
    messageName() {
        return 'subscribeMessage';
    }
    messageBody() {
        return {
            name: `${this.eventMicroserviceName()}.${this.eventName()}`,
            version: '3.0'
        };
    }
    eventMicroserviceName() {
        return 'digital-option-instruments';
    }
    eventName() {
        return 'underlying-list-changed';
    }
    createEvent(data) {
        return new DigitalOptionInstrumentsUnderlyingListChangedV3(data);
    }
}
class SubscribeMessage {
    body;
    constructor(body) {
        this.body = body;
    }
    messageName() {
        return 'subscribeMessage';
    }
    messageBody() {
        return this.body;
    }
    resultOnly() {
        return true;
    }
    createResponse(data) {
        return new Result(data);
    }
}
class UnsubscribeMessage {
    body;
    constructor(body) {
        this.body = body;
    }
    messageName() {
        return 'unsubscribeMessage';
    }
    messageBody() {
        return this.body;
    }
    resultOnly() {
        return true;
    }
    createResponse(data) {
        return new Result(data);
    }
}
class SubscribeBalancesBalanceChangedV1 {
    messageName() {
        return 'subscribeMessage';
    }
    messageBody() {
        return {
            name: `${this.eventMicroserviceName()}.${this.eventName()}`,
            version: '1.0'
        };
    }
    eventMicroserviceName() {
        return 'balances';
    }
    eventName() {
        return 'balance-changed';
    }
    createEvent(data) {
        return new BalancesBalanceChangedV1(data);
    }
}
class SubscribePortfolioPositionChangedV3 {
    userId;
    constructor(userId) {
        this.userId = userId;
    }
    messageName() {
        return 'subscribeMessage';
    }
    messageBody() {
        return {
            name: `${this.eventMicroserviceName()}.${this.eventName()}`,
            version: '3.0',
            params: {
                routingFilters: {
                    user_id: this.userId,
                }
            }
        };
    }
    eventMicroserviceName() {
        return 'portfolio';
    }
    eventName() {
        return 'position-changed';
    }
    createEvent(data) {
        return new PortfolioPositionChangedV3(data);
    }
}
class SubscribePortfolioOrderChangedV2 {
    userId;
    instrumentType;
    constructor(userId, instrumentType) {
        this.userId = userId;
        this.instrumentType = instrumentType;
    }
    messageName() {
        return 'subscribeMessage';
    }
    messageBody() {
        return {
            name: `${this.eventMicroserviceName()}.${this.eventName()}`,
            version: '2.0',
            params: {
                routingFilters: {
                    user_id: this.userId,
                    instrument_type: this.instrumentType
                }
            }
        };
    }
    eventMicroserviceName() {
        return 'portfolio';
    }
    eventName() {
        return 'order-changed';
    }
    createEvent(data) {
        return new PortfolioOrderChangedV2(data);
    }
}
class SubscribeQuoteGeneratedV2 {
    activeId;
    constructor(activeId) {
        this.activeId = activeId;
    }
    messageName() {
        return 'subscribeMessage';
    }
    messageBody() {
        return {
            name: `${this.eventName()}`,
            version: '2.0',
            params: {
                routingFilters: {
                    active_id: this.activeId
                }
            }
        };
    }
    eventMicroserviceName() {
        return 'quotes-ws';
    }
    eventName() {
        return 'quote-generated';
    }
    createEvent(data) {
        return new QuoteGeneratedV2(data);
    }
}
class CallMarginCancelPendingOrderV1 {
    marginInstrumentType;
    orderId;
    constructor(marginInstrumentType, orderId) {
        this.marginInstrumentType = marginInstrumentType;
        this.orderId = orderId;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: `marginal-${this.marginInstrumentType}.cancel-pending-order`,
            version: '1.0',
            body: {
                order_id: this.orderId
            }
        };
    }
    createResponse(data) {
        return new Result(data);
    }
    resultOnly() {
        return true;
    }
}
class CallMarginPlaceStopOrderV1 {
    side;
    userBalanceId;
    count;
    stopPrice;
    instrumentId;
    instrumentActiveId;
    leverage;
    instrumentType;
    stopLoss;
    takeProfit;
    constructor(side, userBalanceId, count, stopPrice, instrumentId, instrumentActiveId, leverage, instrumentType, stopLoss, takeProfit) {
        this.side = side;
        this.userBalanceId = userBalanceId;
        this.count = count;
        this.stopPrice = stopPrice;
        this.instrumentId = instrumentId;
        this.instrumentActiveId = instrumentActiveId;
        this.leverage = leverage;
        this.instrumentType = instrumentType;
        if (stopLoss) {
            this.stopLoss = {
                value: stopLoss.value.toString(),
                type: stopLoss.type,
            };
        }
        if (takeProfit) {
            this.takeProfit = {
                value: takeProfit.value.toString(),
                type: takeProfit.type,
            };
        }
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: `marginal-${this.instrumentType}.place-stop-order`,
            version: '1.0',
            body: {
                side: this.side,
                user_balance_id: this.userBalanceId,
                count: this.count,
                stop_price: this.stopPrice,
                instrument_id: this.instrumentId,
                instrument_active_id: this.instrumentActiveId,
                leverage: this.leverage,
                stop_loss: this.stopLoss,
                take_profit: this.takeProfit
            }
        };
    }
    createResponse(data) {
        return new MarginOrderPlacedV1(data);
    }
    resultOnly() {
        return false;
    }
}
class CallMarginPlaceLimitOrderV1 {
    side;
    userBalanceId;
    count;
    limitPrice;
    instrumentId;
    instrumentActiveId;
    leverage;
    instrumentType;
    stopLoss;
    takeProfit;
    constructor(side, userBalanceId, count, limitPrice, instrumentId, instrumentActiveId, leverage, instrumentType, stopLoss, takeProfit) {
        this.side = side;
        this.userBalanceId = userBalanceId;
        this.count = count;
        this.limitPrice = limitPrice;
        this.instrumentId = instrumentId;
        this.instrumentActiveId = instrumentActiveId;
        this.leverage = leverage;
        this.instrumentType = instrumentType;
        if (stopLoss) {
            this.stopLoss = {
                value: stopLoss.value.toString(),
                type: stopLoss.type,
            };
        }
        if (takeProfit) {
            this.takeProfit = {
                value: takeProfit.value.toString(),
                type: takeProfit.type,
            };
        }
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: `marginal-${this.instrumentType}.place-limit-order`,
            version: '1.0',
            body: {
                side: this.side,
                user_balance_id: this.userBalanceId,
                count: this.count,
                limit_price: this.limitPrice,
                instrument_id: this.instrumentId,
                instrument_active_id: this.instrumentActiveId,
                leverage: this.leverage,
                stop_loss: this.stopLoss,
                take_profit: this.takeProfit
            }
        };
    }
    createResponse(data) {
        return new MarginOrderPlacedV1(data);
    }
    resultOnly() {
        return false;
    }
}
class CallMarginPlaceMarketOrderV1 {
    side;
    userBalanceId;
    count;
    instrumentId;
    instrumentActiveId;
    leverage;
    instrumentType;
    stopLoss;
    takeProfit;
    constructor(side, userBalanceId, count, instrumentId, instrumentActiveId, leverage, instrumentType, stopLoss, takeProfit) {
        this.side = side;
        this.userBalanceId = userBalanceId;
        this.count = count;
        this.instrumentId = instrumentId;
        this.instrumentActiveId = instrumentActiveId;
        this.leverage = leverage;
        this.instrumentType = instrumentType;
        if (stopLoss) {
            this.stopLoss = {
                value: stopLoss.value.toString(),
                type: stopLoss.type,
            };
        }
        if (takeProfit) {
            this.takeProfit = {
                value: takeProfit.value.toString(),
                type: takeProfit.type,
            };
        }
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: `marginal-${this.instrumentType}.place-market-order`,
            version: '1.0',
            body: {
                side: this.side,
                user_balance_id: this.userBalanceId,
                count: this.count,
                instrument_id: this.instrumentId,
                instrument_active_id: this.instrumentActiveId,
                leverage: this.leverage,
                stop_loss: this.stopLoss,
                take_profit: this.takeProfit
            }
        };
    }
    createResponse(data) {
        return new MarginOrderPlacedV1(data);
    }
    resultOnly() {
        return false;
    }
}
class CallMarginInstrumentsGetUnderlyingListV1 {
    marginInstrumentType;
    constructor(marginInstrumentType) {
        this.marginInstrumentType = marginInstrumentType;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: `marginal-${this.marginInstrumentType}-instruments.get-underlying-list`,
            version: '1.0',
            body: {}
        };
    }
    createResponse(data) {
        return new MarginInstrumentsUnderlyingListV1(data);
    }
    resultOnly() {
        return false;
    }
}
class SubscribeMarginInstrumentsUnderlyingListChangedV1 {
    marginInstrumentType;
    constructor(marginInstrumentType) {
        this.marginInstrumentType = marginInstrumentType;
    }
    messageName() {
        return 'subscribeMessage';
    }
    messageBody() {
        return {
            name: `${this.eventMicroserviceName()}.${this.eventName()}`,
            version: '1.0'
        };
    }
    eventMicroserviceName() {
        return `marginal-${this.marginInstrumentType}-instruments`;
    }
    eventName() {
        return 'underlying-list-changed';
    }
    createEvent(data) {
        return new MarginInstrumentsUnderlyingListChangedV1(data);
    }
}
class CallMarginInstrumentsGetInstrumentsListV1 {
    activeId;
    marginInstrumentType;
    constructor(activeId, marginInstrumentType) {
        this.activeId = activeId;
        this.marginInstrumentType = marginInstrumentType;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: `marginal-${this.marginInstrumentType}-instruments.get-instruments-list`,
            version: '1.0',
            body: {
                active_id: this.activeId
            }
        };
    }
    createResponse(data) {
        return new MarginInstrumentsInstrumentsListV1(data);
    }
    resultOnly() {
        return false;
    }
}
class CallMarginGetMarginBalanceV1 {
    userBalanceId;
    constructor(userBalanceId) {
        this.userBalanceId = userBalanceId;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'marginal-portfolio.get-marginal-balance',
            version: '1.0',
            body: {
                user_balance_id: this.userBalanceId
            }
        };
    }
    createResponse(data) {
        return new MarginPortfolioBalanceV1(data);
    }
    resultOnly() {
        return false;
    }
}
class CallSubscribeMarginPortfolioBalanceChangedV1 {
    userBalanceId;
    constructor(userBalanceId) {
        this.userBalanceId = userBalanceId;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'marginal-portfolio.subscribe-balance-changed',
            version: '1.0',
            body: {
                user_balance_id: this.userBalanceId
            }
        };
    }
    createResponse(data) {
        return new Result(data);
    }
    resultOnly() {
        return true;
    }
}
class SubscribeMarginPortfolioBalanceChangedV1 {
    messageName() {
        return 'subscribeMessage';
    }
    messageBody() {
        return {
            name: `${this.eventMicroserviceName()}.${this.eventName()}`,
            version: '1.0'
        };
    }
    eventMicroserviceName() {
        return 'marginal-portfolio';
    }
    eventName() {
        return 'balance-changed';
    }
    createEvent(data) {
        return new MarginPortfolioBalanceV1(data);
    }
}
class CallGetActiveV5 {
    activeId;
    constructor(activeId) {
        this.activeId = activeId;
    }
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'get-active',
            version: '5.0',
            body: {
                id: this.activeId
            }
        };
    }
    createResponse(data) {
        return new ActiveV5(data);
    }
    resultOnly() {
        return false;
    }
}
class ActiveV5 {
    id;
    name;
    image;
    isOtc;
    timeFrom;
    timeTo;
    precision;
    pipScale;
    spreadPlus;
    spreadMinus;
    expirationDays;
    currencyLeftSide;
    currencyRightSide;
    type;
    minQty;
    qtyStep;
    typeQty;
    constructor(data) {
        this.id = data.id;
        this.name = data.name;
        this.image = data.image;
        this.isOtc = data.is_otc;
        this.timeFrom = data.time_from;
        this.timeTo = data.time_to;
        this.precision = data.precision;
        this.pipScale = data.pip_scale;
        this.spreadPlus = data.spread_plus;
        this.spreadMinus = data.spread_minus;
        this.expirationDays = data.expiration_days;
        this.currencyLeftSide = data.currency_left_side;
        this.currencyRightSide = data.currency_right_side;
        this.type = data.type;
        this.minQty = data.min_qty;
        this.qtyStep = data.qty_step;
        this.typeQty = data.type_qty;
    }
}
class CallGetFeaturesV2 {
    messageName() {
        return 'sendMessage';
    }
    messageBody() {
        return {
            name: 'features.get-features',
            version: '2.0',
            body: {
                category: 'client-sdk-js',
            }
        };
    }
    createResponse(data) {
        return new FeaturesV2(data);
    }
    resultOnly() {
        return false;
    }
}
class FeaturesV2 {
    features = [];
    constructor(data) {
        for (const index in data.features) {
            this.features.push(new FeaturesV2Item(data.features[index]));
        }
    }
}
class FeaturesV2Item {
    id;
    name;
    category;
    version;
    status;
    params;
    constructor(msg) {
        this.id = msg.id;
        this.name = msg.name;
        this.category = msg.category;
        this.version = msg.version;
        this.status = msg.status;
        this.params = msg.params;
    }
}
class MarginPortfolioBalanceV1 {
    id;
    type;
    cash;
    bonus;
    currency;
    userId;
    pnl;
    pnlNet;
    equity;
    equityUsd;
    swap;
    dividends;
    margin;
    available;
    marginLevel;
    stopOutLevel;
    constructor(data) {
        this.id = data.id;
        this.type = data.type;
        this.cash = parseFloat(data.cash);
        this.bonus = parseFloat(data.bonus);
        this.currency = data.currency;
        this.userId = data.user_id;
        this.pnl = parseFloat(data.pnl);
        this.pnlNet = parseFloat(data.pnl_net);
        this.equity = parseFloat(data.equity);
        this.equityUsd = parseFloat(data.equity_usd);
        this.swap = parseFloat(data.swap);
        this.dividends = parseFloat(data.dividends);
        this.margin = parseFloat(data.margin);
        this.available = parseFloat(data.available);
        this.marginLevel = parseFloat(data.margin_level);
        this.stopOutLevel = parseFloat(data.stop_out_level);
    }
}
class MarginInstrumentsUnderlyingListV1 {
    items = [];
    constructor(data) {
        for (const index in data.items) {
            const underlying = data.items[index];
            this.items.push(new MarginInstrumentsUnderlyingListV1Item(underlying.active_id, underlying.is_suspended, underlying.name, underlying.schedule));
        }
    }
}
class MarginInstrumentsUnderlyingListChangedV1 {
    type;
    items = [];
    constructor(data) {
        this.type = data.type;
        for (const index in data.items) {
            const underlying = data.items[index];
            this.items.push(new MarginInstrumentsUnderlyingListV1Item(underlying.active_id, underlying.is_suspended, underlying.name, underlying.schedule));
        }
    }
}
class MarginInstrumentsUnderlyingListV1Item {
    activeId;
    isSuspended;
    name;
    schedule;
    constructor(activeId, isSuspended, name, schedule) {
        this.activeId = activeId;
        this.isSuspended = isSuspended;
        this.name = name;
        this.schedule = schedule;
    }
}
class MarginInstrumentsInstrumentsListV1 {
    items = [];
    constructor(data) {
        const dynamicLeverageProfiles = new Map();
        if (data.dynamic_leverage_profiles) {
            for (const index in data.dynamic_leverage_profiles) {
                const dynamicLeverageProfile = data.dynamic_leverage_profiles[index];
                dynamicLeverageProfiles.set(dynamicLeverageProfile.id, dynamicLeverageProfile.items);
            }
        }
        for (const index in data.items) {
            const instrument = data.items[index];
            if (dynamicLeverageProfiles.has(instrument.leverage_profile)) {
                this.items.push(new MarginInstrumentsInstrumentsListV1Item(instrument, dynamicLeverageProfiles.get(instrument.leverage_profile)));
            }
            else {
                this.items.push(new MarginInstrumentsInstrumentsListV1Item(instrument));
            }
        }
    }
}
class MarginInstrumentsInstrumentsListV1DynamicLeverageProfile {
    equity;
    leverage;
    constructor(data) {
        this.equity = data.equity;
        this.leverage = data.leverage;
    }
}
class MarginInstrumentsInstrumentsListV1Item {
    id;
    activeId;
    allowLongPosition;
    allowShortPosition;
    defaultLeverage;
    leverageProfile;
    dynamicLeverageProfile = [];
    isSuspended;
    minQty;
    qtyStep;
    tradable;
    constructor(msg, dynamicLeverageProfile = []) {
        this.id = msg.id;
        this.activeId = msg.active_id;
        this.allowLongPosition = msg.allow_long_position;
        this.allowShortPosition = msg.allow_short_position;
        this.defaultLeverage = msg.default_leverage;
        this.leverageProfile = msg.leverage_profile;
        this.dynamicLeverageProfile = dynamicLeverageProfile;
        this.isSuspended = msg.is_suspended;
        this.minQty = msg.min_qty;
        this.qtyStep = msg.qty_step;
        this.tradable = new MarginInstrumentsInstrumentsListV1Tradable(msg.tradable.from, msg.tradable.to);
    }
}
class MarginInstrumentsInstrumentsListV1Tradable {
    from;
    to;
    constructor(from, to) {
        this.from = from;
        this.to = to;
    }
}
